import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, apiRequest } from '../apiClient';
import { buildUiHelpers } from './sharedUi';
import AgentDownloadControl from './AgentDownloadControl';

const FIELD_LABELS = {
  strategy_key: 'Strategie',
  underlying_symbol: 'Underlying',
  tws_target: 'TWS-Ziel',
  expiry_mode: 'Laufzeit-Modus',
  expiry_date: 'Verfall',
  days_to_expiry: 'Tage bis Verfall',
  right: 'Richtung',
  direction: 'Aktion',
  quantity: 'Anzahl',
  strike: 'Strike',
  selection_mode: 'Annäherung',
  use_exact_dte: 'Use Exact DTE',
  target_option_price: 'Optionspreis-Ziel',
  target_otm_percent: 'OTM Prozent',
  target_atm_offset: 'ATM Offset',
  short_put_delta: 'Delta-Ziel',
  short_call_delta: 'Delta-Ziel',
  spread_width: 'Spread-Breite',
};

const LONG_OPTION_STOP_LOSS_INFO = 'Long-Optionen koennen maximal 100% des gezahlten Premiums verlieren.';
const AUTOMATION_SETUP_WEEKDAYS = [
  { value: 0, label: 'Montag' },
  { value: 1, label: 'Dienstag' },
  { value: 2, label: 'Mittwoch' },
  { value: 3, label: 'Donnerstag' },
  { value: 4, label: 'Freitag' },
  { value: 5, label: 'Samstag' },
  { value: 6, label: 'Sonntag' },
];
const AUTOMATION_SETUP_SYMBOL_FILTERS = ['ALLE', 'SPX', 'SPY', 'QQQ', 'DIA', 'DAX'];
const AUTOMATION_SETUP_WEEKDAY_FILTERS = [
  { value: 'ALLE', label: 'Alle Tage' },
  { value: '0', label: 'Montag' },
  { value: '1', label: 'Dienstag' },
  { value: '2', label: 'Mittwoch' },
  { value: '3', label: 'Donnerstag' },
  { value: '4', label: 'Freitag' },
];
const AUTOMATION_SELECT_STYLE = {
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  backgroundColor: '#d7e3e8',
  color: '#0f172a',
  colorScheme: 'light',
};
const AUTOMATION_SELECT_OPTION_STYLE = {
  color: '#0f172a',
  backgroundColor: '#ffffff',
};
const AUTOMATION_FIELD_STYLE = {
  background: '#d7e3e8',
  backgroundColor: '#d7e3e8',
  color: '#0f172a',
  colorScheme: 'light',
};

function formatStrategyTitle(strategyId) {
  switch (strategyId) {
    case 'single_option':
      return 'Einzelne Option';
    case 'bull_put_spread':
      return 'Bull Put Spread';
    case 'bear_call_spread':
      return 'Bear Call Spread';
    case 'stock':
      return 'Aktie';
    default:
      return strategyId;
  }
}

function getAutomationWeekdayLabel(value) {
  return AUTOMATION_SETUP_WEEKDAYS.find((item) => item.value === Number(value))?.label || '—';
}

function fieldInitialValue(field) {
  if (field.input_type === 'select' && Array.isArray(field.options) && field.options.length > 0) {
    const defaultOption = field.options.find((option) => option.value === field.default_value);
    return defaultOption ? defaultOption.value : field.options[0].value;
  }
  if (field.default_value !== undefined && field.default_value !== null) {
    return String(field.default_value);
  }
  return '';
}

function defaultExitWaitSecondsForSymbol(symbol) {
  return String(symbol || '').trim().toUpperCase() === 'DAX' ? '30' : '5';
}

function formatGermanDateCompact(value) {
  const text = String(value || '').trim();
  if (/^\d{8}$/.test(text)) {
    return `${text.slice(6, 8)}.${text.slice(4, 6)}.${text.slice(0, 4)}`;
  }
  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString('de-DE');
  }
  return text;
}

function formatGermanPrice(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  return numeric.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatGermanStrike(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  return numeric.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}

function formatPreviewOptionCode(leg) {
  const strike = leg?.strike != null ? formatGermanStrike(leg.strike) : '—';
  const right = String(leg?.right || '').trim().toUpperCase();
  return `${strike}${right || ''}`;
}

function getPreviewLegQuantity(leg) {
  const numeric = Number(leg?.quantity);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 1;
}

function getPreviewMultiplier(legs) {
  const candidates = Array.isArray(legs) ? legs : [];
  const raw = candidates.find((leg) => Number.isFinite(Number(leg?.multiplier)) && Number(leg.multiplier) > 0)?.multiplier;
  return Number.isFinite(Number(raw)) && Number(raw) > 0 ? Number(raw) : 100;
}

function getPreviewCurrency(result, legs) {
  const firstLeg = Array.isArray(legs) ? (legs[0] || {}) : {};
  return String(result?.underlying?.currency || firstLeg?.currency || 'USD').trim().toUpperCase() || 'USD';
}

function getPreviewExchange(legs) {
  const exchanges = (Array.isArray(legs) ? legs : [])
    .map((leg) => String(leg?.exchange || '').trim().toUpperCase())
    .filter(Boolean);
  const unique = [...new Set(exchanges)];
  if (unique.length === 1) return unique[0];
  return unique.join(' / ');
}

function getPreviewDteLabel(expiry) {
  const raw = String(expiry || '').trim();
  if (!/^\d{8}$/.test(raw)) return '';
  const year = Number(raw.slice(0, 4));
  const month = Number(raw.slice(4, 6));
  const day = Number(raw.slice(6, 8));
  const expiryDate = new Date(year, month - 1, day);
  if (Number.isNaN(expiryDate.getTime())) return '';
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const diffMs = expiryDate.getTime() - startOfToday.getTime();
  const dte = Math.max(0, Math.round(diffMs / 86400000));
  return `${dte} DTE`;
}

function buildPreviewDetailLines({ strategyKey, symbol, expiry, legs }) {
  const normalizedStrategyKey = String(strategyKey || '').trim().toLowerCase();
  const expiryLabel = expiry ? formatGermanDateCompact(expiry) : '—';
  const dteLabel = getPreviewDteLabel(expiry);
  const normalizedLegs = Array.isArray(legs) ? legs.filter(Boolean) : [];
  if (normalizedStrategyKey === 'stock' && normalizedLegs.length === 1) {
    const leg = normalizedLegs[0] || {};
    const action = String(leg.action || '').trim().toUpperCase() === 'SELL' ? 'SELL' : 'BUY';
    const qty = formatGermanStrike(getPreviewLegQuantity(leg));
    const exchange = String(leg.exchange || '').trim().toUpperCase();
    const primaryExchange = String(leg.primary_exchange || leg.primaryExchange || '').trim().toUpperCase();
    const currency = String(leg.currency || '').trim().toUpperCase();
    const orderType = String(leg.order_type || '').trim().toUpperCase() === 'LMT' ? 'Limit' : 'Market';
    const routeParts = [exchange, currency, primaryExchange].filter(Boolean).join(' ');
    return [
      `${action} ${qty} ${symbol} Aktie`,
      `${orderType}${routeParts ? ` · ${routeParts}` : ''}`,
    ];
  }
  const sellLeg = normalizedLegs.find((leg) => String(leg?.action || '').trim().toUpperCase() === 'SELL') || normalizedLegs[0] || {};
  const buyLeg = normalizedLegs.find((leg) => String(leg?.action || '').trim().toUpperCase() === 'BUY') || normalizedLegs[1] || {};
  let headline = `${expiryLabel} · ${formatStrategyTitle(strategyKey)}`;
  if (normalizedStrategyKey === 'bull_put_spread' && sellLeg?.strike != null && buyLeg?.strike != null) {
    headline = `${symbol} ${expiryLabel} ${formatGermanStrike(sellLeg.strike)}/${formatGermanStrike(buyLeg.strike)} Bull-Put${dteLabel ? ` · ${dteLabel}` : ''}`;
  } else if (normalizedStrategyKey === 'bear_call_spread' && sellLeg?.strike != null && buyLeg?.strike != null) {
    headline = `${symbol} ${expiryLabel} ${formatGermanStrike(sellLeg.strike)}/${formatGermanStrike(buyLeg.strike)} Bear-Call${dteLabel ? ` · ${dteLabel}` : ''}`;
  } else if (normalizedStrategyKey === 'single_option' && normalizedLegs.length === 1) {
    headline = `${symbol} ${expiryLabel} ${String(sellLeg?.action || '').trim().toUpperCase() || '—'} ${formatPreviewOptionCode(sellLeg)}${dteLabel ? ` · ${dteLabel}` : ''}`;
  }
  const legLines = normalizedLegs.map((leg) => {
    const action = String(leg?.action || '').trim().toUpperCase() || '—';
    const qty = formatGermanStrike(getPreviewLegQuantity(leg));
    const exchange = String(leg?.exchange || '').trim().toUpperCase();
    const midPrice = Number(leg?.mid_price);
    const midText = Number.isFinite(midPrice) ? ` · Mid ${formatGermanPrice(midPrice)}` : '';
    return `${action} ${qty} ${formatPreviewOptionCode(leg)}${exchange ? ` @${exchange}` : ''}${midText}`;
  });
  return [headline, ...legLines];
}

function buildPreviewInfoLines({ result, legs, warnings, errorMessage }) {
  if (String(errorMessage || '').trim()) return [String(errorMessage || '').trim()];
  const normalizedLegs = Array.isArray(legs) ? legs.filter(Boolean) : [];
  if (String(result?.strategy_key || '').trim().toLowerCase() === 'stock' && normalizedLegs.length === 1) {
    const leg = normalizedLegs[0] || {};
    const quantity = getPreviewLegQuantity(leg);
    const orderType = String(leg.order_type || '').trim().toUpperCase() === 'LMT' ? 'Limit' : 'Market';
    const limitPrice = Number(leg.limit_price);
    const currency = String(leg.currency || result?.currency || '').trim().toUpperCase();
    const base = [`Qty ${formatGermanStrike(quantity)}`, orderType];
    if (Number.isFinite(limitPrice) && limitPrice > 0) base.push(`Limit ${formatMoneyValue(limitPrice, currency)}`);
    const warningLines = Array.isArray(warnings)
      ? warnings.filter(Boolean).map((entry) => String(entry).trim()).filter(Boolean)
      : [];
    return warningLines.length > 0 ? [base.join(' · '), ...warningLines] : [base.join(' · ')];
  }
  const currency = getPreviewCurrency(result, normalizedLegs);
  const exchange = getPreviewExchange(normalizedLegs);
  const multiplier = getPreviewMultiplier(normalizedLegs);
  const quantity = getPreviewLegQuantity(normalizedLegs[0] || {});
  const netMid = normalizedLegs.reduce((sum, leg) => {
    const midPrice = Number(leg?.mid_price);
    if (!Number.isFinite(midPrice)) return sum;
    const action = String(leg?.action || '').trim().toUpperCase();
    return sum + (action === 'BUY' ? midPrice : -midPrice);
  }, 0);
  const netAmount = Number.isFinite(netMid) ? netMid * quantity * multiplier : null;
  const baseLines = [];
  if (normalizedLegs.length > 0) {
    const quoteParts = [`${normalizedLegs.length} Legs`, `Qty ${formatGermanStrike(quantity)}`];
    if (exchange) quoteParts.push(`Routing ${exchange}`);
    if (Number.isFinite(multiplier)) quoteParts.push(`Mult. ${formatGermanStrike(multiplier)}`);
    baseLines.push(quoteParts.join(' · '));
  }
  if (normalizedLegs.length > 0 && normalizedLegs.every((leg) => Number.isFinite(Number(leg?.mid_price)))) {
    const pricingParts = [`Netto-Mid ${formatMoneyValue(netMid, currency)}`];
    if (netAmount != null) pricingParts.push(`Betrag ca. ${formatMoneyValue(netAmount, currency)}`);
    baseLines.push(pricingParts.join(' · '));
  }
  const warningLines = Array.isArray(warnings)
    ? warnings.filter(Boolean).map((entry) => String(entry).trim()).filter(Boolean)
    : [];
  return warningLines.length > 0 ? [...baseLines, ...warningLines] : (baseLines.length > 0 ? baseLines : ['—']);
}

function compareValues(left, right, direction = 'asc') {
  const multiplier = direction === 'asc' ? 1 : -1;
  if (left == null && right == null) return 0;
  if (left == null) return 1 * multiplier;
  if (right == null) return -1 * multiplier;
  if (typeof left === 'number' && typeof right === 'number') return (left - right) * multiplier;
  return String(left).localeCompare(String(right), 'de', { numeric: true, sensitivity: 'base' }) * multiplier;
}

function buildOptionsProfile(item, legs) {
  const strategyKey = String(item?.strategy_key || '').trim().toLowerCase();
  if (strategyKey === 'bull_put_spread') return 'Bull Put Spread';
  if (strategyKey === 'bear_call_spread') return 'Bear Call Spread';
  if (strategyKey !== 'single_option' || !Array.isArray(legs) || legs.length !== 1) return formatStrategyTitle(item?.strategy_key || '');
  const leg = legs[0] || {};
  const action = String(leg.action || '').trim().toUpperCase();
  const right = String(leg.right || '').trim().toUpperCase();
  if (action === 'BUY' && right === 'C') return 'Long Call';
  if (action === 'SELL' && right === 'C') return 'Short Call';
  if (action === 'BUY' && right === 'P') return 'Long Put';
  if (action === 'SELL' && right === 'P') return 'Short Put';
  return 'Einzeloption';
}

function isLongOptionDraft(strategyKey, draftValues) {
  return String(strategyKey || '').trim().toLowerCase() === 'single_option'
    && String(draftValues?.direction || '').trim().toUpperCase() === 'BUY';
}

function isLongOptionTrade(row) {
  return String(row?.entryAction || '').trim().toUpperCase() === 'BUY';
}

function getLongOptionStopLossError(stopLossPercent, isLongOption) {
  if (!isLongOption || stopLossPercent == null) return '';
  return Number(stopLossPercent) > 100
    ? LONG_OPTION_STOP_LOSS_INFO
    : '';
}

function formatSignedQuantity(quantity, entryAction) {
  const numeric = Number(quantity);
  if (!Number.isFinite(numeric) || numeric === 0) return 'Qty —';
  const direction = String(entryAction || '').trim().toUpperCase();
  const signedQuantity = direction === 'SELL' ? -Math.abs(numeric) : Math.abs(numeric);
  return `${signedQuantity > 0 ? '+' : ''}${formatGermanStrike(signedQuantity)}`;
}

function formatGermanPercent(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  return numeric.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function getPnlColor(value, colors) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric === 0) return colors.muted;
  return numeric > 0 ? colors.green : colors.red;
}

function getExecutionDisplayState(status, brokerStatus = '') {
  const normalizedStatus = String(status || '').toLowerCase();
  const normalizedBrokerStatus = String(brokerStatus || '').toLowerCase();
  if (normalizedBrokerStatus === 'pendingsubmit') return 'pending_submit';
  if (normalizedBrokerStatus === 'presubmitted') return 'pre_submitted';
  if (normalizedBrokerStatus === 'submitted') return 'submitted';
  return normalizedStatus;
}

function renderTradeStatusLabel(status, brokerStatus = '') {
  switch (getExecutionDisplayState(status, brokerStatus)) {
    case 'approved':
      return 'Freigegeben';
    case 'executing':
      return 'Wird gesendet';
    case 'pending_submit':
      return 'PendingSubmit in TWS';
    case 'pre_submitted':
      return 'PreSubmitted in TWS';
    case 'submitted':
      return 'Submitted in TWS';
    case 'partially_filled':
      return 'Teilweise gefüllt';
    case 'filled':
      return 'Gefüllt';
    default:
      return status || '—';
  }
}

function formatMoneyValue(value, currency = '') {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return `${formatGermanPrice(numeric)}${currency ? ` ${currency}` : ''}`;
}

function formatNullablePrice(value, currency = '') {
  if (value == null || value === '' || !Number.isFinite(Number(value))) return '—';
  return formatMoneyValue(Number(value), currency);
}

function formatDateValue(value) {
  if (!value) return '—';
  const text = String(value).trim();
  const normalized = /^\d{8}$/.test(text) ? `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)}` : text;
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatCompactNumberValue(value, digits = 0) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return numeric.toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function formatRiskValue(value, currency) {
  if (value === 'UNLIMITED') return 'Unbegrenzt';
  return formatMoneyValue(value, currency);
}

function isStockAutomationTrade(row) {
  const contract = row?.contract || {};
  const secType = String(contract.sec_type || contract.secType || contract.security_type || '').trim().toUpperCase();
  return secType === 'STK' || String(row?.strategy_key || row?.strategyKey || '').trim().toLowerCase() === 'stock';
}

function chooseStockLimitPriceFromQuote(quote, action) {
  const normalizedAction = String(action || '').trim().toUpperCase();
  const candidates = normalizedAction === 'SELL'
    ? [quote?.mid, quote?.bid, quote?.last, quote?.close, quote?.ask]
    : [quote?.mid, quote?.ask, quote?.last, quote?.close, quote?.bid];
  const numeric = candidates.map(Number).find((value) => Number.isFinite(value) && value > 0);
  return numeric == null ? '' : String(numeric);
}

function inferAutomationMultiplier(source) {
  if (isStockAutomationTrade(source)) return 1;
  const rawCandidates = [
    source?.multiplier,
    source?.contract?.multiplier,
    source?.tradingClass,
    source?.contract?.tradingClass,
    source?.symbol,
    source?.contract?.symbol,
    source?.localSymbol,
    source?.contract?.localSymbol,
    source?.local_symbol,
    source?.contract?.local_symbol,
  ];
  const numericCandidate = rawCandidates.find((value) => value != null && value !== '' && Number.isFinite(Number(value)) && Number(value) > 0);
  if (numericCandidate != null) return Number(numericCandidate);
  const combinedText = rawCandidates
    .map((value) => String(value || '').trim().toUpperCase())
    .filter(Boolean)
    .join(' ');
  if (combinedText.includes('ODAX') || combinedText.includes(' DAX')) return 5;
  return 100;
}

function getDetailMultiplier(row) {
  if (isStockAutomationTrade(row)) return 1;
  const rawMultiplier = row?.multiplier ?? row?.contract?.multiplier ?? row?.contract?.legs?.[0]?.multiplier;
  if (rawMultiplier != null && rawMultiplier !== '' && Number.isFinite(Number(rawMultiplier))) {
    const numeric = Number(rawMultiplier);
    if (numeric > 1) return numeric;
  }
  const legMultiplier = row?.contract?.legs?.find((leg) => Number.isFinite(Number(leg?.multiplier)) && Number(leg.multiplier) > 1)?.multiplier;
  if (legMultiplier != null && legMultiplier !== '' && Number.isFinite(Number(legMultiplier))) {
    return Number(legMultiplier);
  }
  return inferAutomationMultiplier(row);
}

function getAutomationContractLegs(row) {
  const rawLegs = Array.isArray(row?.contract?.legs) ? row.contract.legs : [];
  return rawLegs
    .map((leg) => {
      const strike = Number(leg?.strike);
      const quantity = Number(leg?.quantity ?? row?.currentQuantity ?? row?.quantity ?? 0);
      const multiplier = Number(leg?.multiplier ?? row?.multiplier ?? row?.contract?.multiplier ?? inferAutomationMultiplier({ ...row, ...leg }));
      const action = String(leg?.action || '').trim().toUpperCase();
      const right = String(leg?.right || leg?.put_call || leg?.type || '').trim().toUpperCase();
      if (!Number.isFinite(strike) || !Number.isFinite(quantity) || quantity <= 0 || !right) return null;
      return {
        ...leg,
        strike,
        quantity,
        multiplier: Number.isFinite(multiplier) && multiplier > 0 ? multiplier : inferAutomationMultiplier({ ...row, ...leg }),
        action,
        right,
        expiry: leg?.expiry || row?.expiry || row?.contract?.expiry || null,
        currency: String(leg?.currency || row?.currency || row?.contract?.currency || 'USD').trim().toUpperCase() || 'USD',
        symbol: String(leg?.symbol || row?.symbol || row?.contract?.symbol || '').trim().toUpperCase() || row?.symbol || '—',
      };
    })
    .filter(Boolean);
}

function resolveAutomationCurrentUnderlyingPrice(row) {
  const candidates = [
    row?.current_underlying_price,
    row?.currentUnderlyingPrice,
    row?.contract?.current_underlying_price,
    row?.contract?.currentUnderlyingPrice,
    ...(Array.isArray(row?.contract?.legs)
      ? row.contract.legs.flatMap((leg) => [
        leg?.current_underlying_price,
        leg?.currentUnderlyingPrice,
        leg?.underlying_price,
        leg?.underlyingPrice,
      ])
      : []),
  ];
  const match = candidates.find((value) => Number.isFinite(Number(value)));
  return match != null ? Number(match) : null;
}

function resolveAutomationUnderlyingPriceCurrency(row) {
  const candidates = [
    row?.underlying_price_currency,
    row?.underlyingPriceCurrency,
    row?.contract?.underlying_price_currency,
    row?.contract?.underlyingPriceCurrency,
    ...(Array.isArray(row?.contract?.legs)
      ? row.contract.legs.flatMap((leg) => [
        leg?.underlying_price_currency,
        leg?.underlyingPriceCurrency,
        leg?.currency,
      ])
      : []),
    row?.contract?.currency,
    row?.currency,
  ];
  const match = candidates.find((value) => String(value || '').trim() !== '');
  return String(match || 'USD').trim().toUpperCase();
}

function buildAutomationOpenOptionLegs(row, baseCurrency = 'USD') {
  const contractLegs = getAutomationContractLegs(row);
  if (contractLegs.length > 0) {
    const comboEntryPrice = Math.abs(Number(row?.entryPrice || 0));
    const defaultQuantity = Math.max(1, Math.abs(Number(row?.currentQuantity ?? row?.quantity ?? contractLegs[0]?.quantity ?? 1)));
    const allocatedEntryValue = comboEntryPrice > 0
      ? contractLegs.reduce((sum, leg) => {
        const qty = Math.abs(Number(leg.quantity || defaultQuantity));
        const multiplier = Number(leg.multiplier || inferAutomationMultiplier(leg));
        const referencePrice = Number(leg.mid_price ?? leg.limit_price ?? 0);
        return sum + (Math.abs(referencePrice) * qty * multiplier);
      }, 0)
      : 0;
    return contractLegs.map((leg) => {
      const qty = Math.abs(Number(leg.quantity || defaultQuantity));
      const multiplier = Number(leg.multiplier || inferAutomationMultiplier(leg));
      const referencePrice = Number(leg.mid_price ?? leg.limit_price ?? 0);
      const referenceValue = Math.abs(referencePrice) * qty * multiplier;
      const openValueBase = comboEntryPrice > 0
        ? (
          allocatedEntryValue > 0
            ? (comboEntryPrice * qty * multiplier) * (referenceValue / allocatedEntryValue)
            : ((comboEntryPrice * qty * multiplier) / contractLegs.length)
        )
        : referenceValue;
      return {
        type: leg.right,
        expiry: leg.expiry,
        strike: leg.strike,
        currency: leg.currency || baseCurrency,
        fxRate: 1,
        netQty: leg.action === 'BUY' ? qty : -qty,
        openValueBase,
        openCommissionBase: 0,
        multiplier,
        symbol: leg.symbol,
        action: leg.action,
      };
    });
  }
  const strike = row?.strike != null ? Number(row.strike) : null;
  if (strike == null || !Number.isFinite(strike)) return [];
  const qty = Math.abs(Number(row?.quantity || row?.currentQuantity || 0));
  if (!Number.isFinite(qty) || qty <= 0) return [];
  const entryPrice = Number(row?.entryPrice || 0);
  const multiplier = getDetailMultiplier(row);
  const isLong = String(row?.entryAction || '').trim().toUpperCase() === 'BUY';
  return [{
    type: String(row?.right || row?.contract?.right || '').trim().toUpperCase(),
    expiry: row?.expiry || row?.contract?.expiry || null,
    strike,
    currency: String(row?.currency || row?.contract?.currency || baseCurrency).trim().toUpperCase() || baseCurrency,
    fxRate: 1,
    netQty: isLong ? qty : -qty,
    openValueBase: Math.abs(qty) * Math.abs(entryPrice) * multiplier,
    openCommissionBase: 0,
    multiplier,
    symbol: String(row?.symbol || row?.contract?.symbol || '').trim().toUpperCase() || row?.symbol || '—',
    action: isLong ? 'BUY' : 'SELL',
  }];
}

function calculateAutomationOptionRisk(row, baseCurrency = 'USD') {
  const activeLegs = buildAutomationOpenOptionLegs(row, baseCurrency);
  if (activeLegs.length !== 1) return null;
  const leg = activeLegs[0];
  const qty = Math.abs(Number(leg.netQty || 0));
  const openDebit = Number(leg.openValueBase || 0) + Number(leg.openCommissionBase || 0);
  const openCredit = Math.max(0, Number(leg.openValueBase || 0) - Number(leg.openCommissionBase || 0));
  const strikeBaseValue = leg.strike != null ? Number(leg.strike) * getDetailMultiplier(leg) * qty * Number(leg.fxRate || 1) : null;
  if (leg.netQty > 0 && leg.type === 'C') return { maxWin: 'UNLIMITED', maxLoss: openDebit };
  if (leg.netQty < 0 && leg.type === 'C') return { maxWin: openCredit, maxLoss: 'UNLIMITED' };
  if (leg.netQty > 0 && leg.type === 'P') return { maxWin: strikeBaseValue != null ? Math.max(0, strikeBaseValue - openDebit) : null, maxLoss: openDebit };
  if (leg.netQty < 0 && leg.type === 'P') return { maxWin: openCredit, maxLoss: strikeBaseValue != null ? Math.max(0, strikeBaseValue - openCredit) : null };
  return null;
}

function getAutomationCommissionTotal(row) {
  const directCandidates = [
    row?.commission,
    row?.commissions,
    row?.entryCommission,
    row?.entry_commission,
    row?.contract?.commission,
    row?.contract?.commissions,
    row?.contract?.entry_commission,
  ];
  const directValue = directCandidates.find((value) => Number.isFinite(Number(value)) && Number(value) !== 0);
  if (directValue != null) return Math.abs(Number(directValue));

  const fillItems = Array.isArray(row?.contract?.fills) ? row.contract.fills : [];
  const fillTotal = fillItems.reduce((sum, fill) => {
    const commission = Math.abs(Number(fill?.commission || 0));
    const fees = Math.abs(Number(fill?.fees || fill?.fees_total || 0));
    return sum + commission + fees;
  }, 0);
  return fillTotal > 0 ? fillTotal : null;
}

function calculateAutomationSpreadRisk(row, baseCurrency = 'USD') {
  const activeLegs = buildAutomationOpenOptionLegs(row, baseCurrency);
  if (activeLegs.length < 2) return null;
  const quantity = Math.max(1, Math.abs(Number(row?.currentQuantity ?? row?.quantity ?? activeLegs[0]?.quantity ?? 1)));
  const multiplier = getDetailMultiplier(row);
  const strikes = activeLegs
    .map((leg) => Number(leg.strike))
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b);
  if (strikes.length < 2 || !Number.isFinite(Number(multiplier)) || multiplier <= 0) return null;

  const widestStrikeDistance = Math.max(...strikes.map((strike, index) => (
    index === 0 ? 0 : Math.abs(strike - strikes[index - 1])
  )));
  if (!Number.isFinite(widestStrikeDistance) || widestStrikeDistance <= 0) return null;

  const grossWidthValue = widestStrikeDistance * multiplier * quantity;
  const entryPrice = Math.abs(Number(row?.entryPrice || 0));
  const entryValue = Number.isFinite(entryPrice) && entryPrice > 0 ? entryPrice * multiplier * quantity : 0;
  const entryAction = String(row?.entryAction || row?.contract?.entry_combo_action || '').trim().toUpperCase();
  const strategyKey = String(row?.strategyKey || row?.strategy_key || row?.contract?.strategy_key || '').trim().toLowerCase();
  const commissions = getAutomationCommissionTotal(row) || 0;

  if (strategyKey === 'bull_put_spread' || strategyKey === 'bear_call_spread' || entryAction === 'SELL') {
    return {
      maxWin: Math.max(0, entryValue - commissions),
      maxLoss: Math.max(0, grossWidthValue - entryValue + commissions),
    };
  }
  if (entryAction === 'BUY') {
    return {
      maxWin: Math.max(0, grossWidthValue - entryValue - commissions),
      maxLoss: Math.max(0, entryValue + commissions),
    };
  }
  return null;
}

function buildAutomationOptionProfile(row, currentUnderlyingPrice = null, currentLivePnlBase = null, mode = 'expiry') {
  const openLegs = buildAutomationOpenOptionLegs(row, row?.currency || row?.contract?.currency || 'USD');
  if (!openLegs.length) return null;
  const strikes = openLegs.map((leg) => Number(leg.strike)).filter((value) => Number.isFinite(value));
  if (!strikes.length) return null;
  const minStrike = Math.min(...strikes);
  const maxStrike = Math.max(...strikes);
  const reference = currentUnderlyingPrice != null ? Number(currentUnderlyingPrice) : ((minStrike + maxStrike) / 2);
  const anchoredMin = currentUnderlyingPrice != null ? Math.min(minStrike, Number(currentUnderlyingPrice)) : minStrike;
  const anchoredMax = currentUnderlyingPrice != null ? Math.max(maxStrike, Number(currentUnderlyingPrice)) : maxStrike;
  const strikeWidth = Math.max(anchoredMax - anchoredMin, maxStrike - minStrike, Math.abs(reference) * 0.08, 1);
  const padding = Math.max(strikeWidth * 1.1, Math.abs(reference) * 0.08, 12);
  let minPrice = Math.max(0, anchoredMin - padding);
  let maxPrice = anchoredMax + padding;
  const pointCount = 81;

  const payoffAtPrice = (priceAtExpiry) => openLegs.reduce((sum, leg) => {
    const qty = Math.abs(Number(leg.netQty || 0));
    const multiplier = getDetailMultiplier(leg);
    const strike = Number(leg.strike || 0);
    const intrinsic = leg.type === 'C' ? Math.max(priceAtExpiry - strike, 0) : Math.max(strike - priceAtExpiry, 0);
    const intrinsicBase = intrinsic * multiplier * qty;
    const longCostBase = Number(leg.openValueBase || 0) + Number(leg.openCommissionBase || 0);
    const shortCreditBase = Number(leg.openValueBase || 0) - Number(leg.openCommissionBase || 0);
    return leg.netQty > 0 ? sum + (intrinsicBase - longCostBase) : sum + (shortCreditBase - intrinsicBase);
  }, 0);

  const buildPoints = (rangeMin, rangeMax) => Array.from({ length: pointCount }, (_value, index) => {
    const ratio = pointCount === 1 ? 0 : index / (pointCount - 1);
    const underlying = rangeMin + ((rangeMax - rangeMin) * ratio);
    return { underlying, pnl: payoffAtPrice(underlying) };
  });

  let basePoints = buildPoints(minPrice, maxPrice);
  const hasBreakEvenInPoints = (points) => points.some((point, index) => {
    if (index === 0) return false;
    const previous = points[index - 1];
    return Math.abs(previous.pnl) <= 1e-9 || Math.abs(point.pnl) <= 1e-9 || ((previous.pnl < 0 && point.pnl > 0) || (previous.pnl > 0 && point.pnl < 0));
  });
  if (!hasBreakEvenInPoints(basePoints)) {
    const span = Math.max(maxPrice - minPrice, 1);
    if (openLegs.every((leg) => leg.type === 'P')) minPrice = Math.max(0, minPrice - (span * 0.9));
    else maxPrice += span * 0.9;
    basePoints = buildPoints(minPrice, maxPrice);
  }

  const shiftedPoints = (mode === 'today' && currentUnderlyingPrice != null && currentLivePnlBase != null)
    ? (() => {
        const anchorPnl = payoffAtPrice(Number(currentUnderlyingPrice));
        const offset = Number(currentLivePnlBase) - anchorPnl;
        return basePoints.map((point) => ({ ...point, pnl: point.pnl + offset }));
      })()
    : basePoints;

  const breakEvens = [];
  for (let index = 1; index < shiftedPoints.length; index += 1) {
    const previous = shiftedPoints[index - 1];
    const current = shiftedPoints[index];
    if ((previous.pnl < 0 && current.pnl > 0) || (previous.pnl > 0 && current.pnl < 0)) {
      const span = current.pnl - previous.pnl;
      const ratio = Math.abs(span) > 1e-9 ? ((0 - previous.pnl) / span) : 0;
      breakEvens.push(previous.underlying + ((current.underlying - previous.underlying) * ratio));
    }
  }

  const pnls = shiftedPoints.map((point) => point.pnl);
  return {
    expiry: openLegs[0]?.expiry || null,
    points: shiftedPoints,
    minPrice,
    maxPrice,
    minPnl: Math.min(...pnls, 0),
    maxPnl: Math.max(...pnls, 0),
    breakEvens,
    strikes: strikes.sort((a, b) => a - b),
    currentUnderlyingPrice: currentUnderlyingPrice != null ? Number(currentUnderlyingPrice) : null,
    currentLivePnlBase: currentLivePnlBase != null ? Number(currentLivePnlBase) : null,
    expiryPnlAtCurrentUnderlying: currentUnderlyingPrice != null ? payoffAtPrice(Number(currentUnderlyingPrice)) : null,
    priceCurrency: row?.underlyingPriceCurrency || row?.currency || row?.contract?.currency || '',
    mode,
    legs: openLegs,
  };
}

function buildBreakEvenDistance(profile) {
  const currentUnderlying = Number(profile?.currentUnderlyingPrice);
  const breakEvens = Array.isArray(profile?.breakEvens) ? profile.breakEvens.filter((value) => Number.isFinite(Number(value))) : [];
  if (!Number.isFinite(currentUnderlying) || !breakEvens.length) return null;
  const nearest = breakEvens.reduce((best, value) => (best == null || Math.abs(Number(value) - currentUnderlying) < Math.abs(Number(best) - currentUnderlying) ? Number(value) : best), null);
  if (!Number.isFinite(nearest)) return null;
  const distance = currentUnderlying - nearest;
  const absDistance = Math.abs(distance);
  const percent = Math.abs(nearest) > 1e-9 ? (absDistance / Math.abs(nearest)) * 100 : null;
  return { nearest, distance, absDistance, percent, direction: distance > 0 ? 'über' : distance < 0 ? 'unter' : 'auf' };
}

function interpolateProfilePnl(points, price) {
  if (!Array.isArray(points) || !points.length || !Number.isFinite(Number(price))) return null;
  const target = Number(price);
  if (target <= Number(points[0].underlying)) return Number(points[0].pnl);
  if (target >= Number(points[points.length - 1].underlying)) return Number(points[points.length - 1].pnl);
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    if (target < Number(previous.underlying) || target > Number(current.underlying)) continue;
    const x1 = Number(previous.underlying);
    const x2 = Number(current.underlying);
    const y1 = Number(previous.pnl);
    const y2 = Number(current.pnl);
    const ratio = Math.abs(x2 - x1) > 1e-9 ? (target - x1) / (x2 - x1) : 0;
    return y1 + ((y2 - y1) * ratio);
  }
  return null;
}

function stdNormPdf(x) {
  return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);
}

function stdNormCdf(x) {
  const sign = x < 0 ? -1 : 1;
  const absX = Math.abs(x) / Math.sqrt(2);
  const t = 1 / (1 + 0.3275911 * absX);
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const erf = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-absX * absX);
  return 0.5 * (1 + sign * erf);
}

function blackScholesPrice(type, s, k, t, r, sigma) {
  if (t <= 0 || sigma <= 0 || s <= 0 || k <= 0) return type === 'C' ? Math.max(s - k, 0) : Math.max(k - s, 0);
  const sqrtT = Math.sqrt(t);
  const d1 = (Math.log(s / k) + (r + 0.5 * sigma * sigma) * t) / (sigma * sqrtT);
  const d2 = d1 - sigma * sqrtT;
  if (type === 'C') return s * stdNormCdf(d1) - k * Math.exp(-r * t) * stdNormCdf(d2);
  return k * Math.exp(-r * t) * stdNormCdf(-d2) - s * stdNormCdf(-d1);
}

function impliedVolatility(type, s, k, t, r, marketPrice) {
  if (!Number.isFinite(marketPrice) || marketPrice <= 0 || s <= 0 || k <= 0 || t <= 0) return null;
  let low = 0.0001;
  let high = 5.0;
  for (let i = 0; i < 80; i += 1) {
    const mid = (low + high) / 2;
    const price = blackScholesPrice(type, s, k, t, r, mid);
    if (Math.abs(price - marketPrice) < 1e-5) return mid;
    if (price > marketPrice) high = mid;
    else low = mid;
  }
  return (low + high) / 2;
}

function calculateApproxGreeks(profile, row) {
  const underlying = Number(row?.currentUnderlyingPrice);
  const contractLegs = Array.isArray(row?.contractLegs) ? row.contractLegs : [];
  if (!profile || !Number.isFinite(underlying) || contractLegs.length === 0) return null;
  const now = new Date();
  const rate = 0.02;
  const totals = { delta: 0, gamma: 0, theta: 0, vega: 0 };
  let usedLegs = 0;

  contractLegs.forEach((leg) => {
    const type = String(leg?.right || leg?.put_call || leg?.type || '').trim().toUpperCase();
    const strike = Number(leg?.strike);
    const qty = Number(leg?.action === 'BUY' ? Math.abs(Number(leg?.quantity || 0)) : -Math.abs(Number(leg?.quantity || 0)));
    const multiplier = Number(leg?.multiplier || inferAutomationMultiplier(leg) || 1);
    const mark = Number(
      leg?.current_mark_price
      ?? leg?.mark_price
      ?? leg?.mid_price
      ?? leg?.last_price
      ?? leg?.last
    );
    const expiryRaw = leg?.expiry || row?.expiry || row?.contract?.expiry;
    if (!['C', 'P'].includes(type) || !Number.isFinite(strike) || !Number.isFinite(qty) || !Number.isFinite(mark)) return;
    const expiry = expiryRaw ? new Date(expiryRaw) : null;
    if (!expiry || Number.isNaN(expiry.getTime())) return;
    const t = Math.max((expiry.getTime() - now.getTime()) / (365.25 * 24 * 3600 * 1000), 1 / 365.25);
    const iv = impliedVolatility(type, underlying, strike, t, rate, mark);
    if (!Number.isFinite(iv) || iv <= 0) return;
    const sqrtT = Math.sqrt(t);
    const d1 = (Math.log(underlying / strike) + (rate + 0.5 * iv * iv) * t) / (iv * sqrtT);
    const d2 = d1 - iv * sqrtT;
    const pdf = stdNormPdf(d1);
    const directionQty = qty * multiplier;
    const delta = type === 'C' ? stdNormCdf(d1) : (stdNormCdf(d1) - 1);
    const gamma = pdf / (underlying * iv * sqrtT);
    const thetaYear = type === 'C'
      ? (-(underlying * pdf * iv) / (2 * sqrtT) - rate * strike * Math.exp(-rate * t) * stdNormCdf(d2))
      : (-(underlying * pdf * iv) / (2 * sqrtT) + rate * strike * Math.exp(-rate * t) * stdNormCdf(-d2));
    const vega = underlying * pdf * sqrtT;
    totals.delta += delta * directionQty;
    totals.gamma += gamma * directionQty;
    totals.theta += (thetaYear / 365) * directionQty;
    totals.vega += (vega / 100) * directionQty;
    usedLegs += 1;
  });

  if (usedLegs === 0) return null;
  return { ...totals, usedLegs };
}

function OptionProfileChart({ profile, currency, COLORS, valueColor, scenarioPrice = null }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const defsId = useMemo(() => `option-profile-${Math.random().toString(36).slice(2, 10)}`, []);
  if (!profile) return null;
  const width = 760;
  const height = 240;
  const paddingLeft = 52;
  const paddingRight = 20;
  const paddingTop = 16;
  const paddingBottom = 28;
  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;
  const minX = profile.minPrice;
  const maxX = profile.maxPrice;
  const minY = Math.min(profile.minPnl, 0);
  const maxY = Math.max(profile.maxPnl, 0);
  const ySpan = Math.max(maxY - minY, 1);
  const xSpan = Math.max(maxX - minX, 1);
  const toX = (value) => paddingLeft + (((value - minX) / xSpan) * chartWidth);
  const toY = (value) => paddingTop + (((maxY - value) / ySpan) * chartHeight);
  const zeroY = toY(0);
  const path = profile.points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${toX(point.underlying).toFixed(2)} ${toY(point.pnl).toFixed(2)}`).join(' ');
  const areaPath = `${path} L ${toX(profile.points[profile.points.length - 1].underlying).toFixed(2)} ${zeroY.toFixed(2)} L ${toX(profile.points[0].underlying).toFixed(2)} ${zeroY.toFixed(2)} Z`;
  const currentPoint = profile.currentUnderlyingPrice != null
    ? {
        underlying: profile.currentUnderlyingPrice,
        pnl: profile.points.reduce((closest, point) => (
          Math.abs(point.underlying - profile.currentUnderlyingPrice) < Math.abs(closest.underlying - profile.currentUnderlyingPrice)
            ? point
            : closest
        ), profile.points[0]).pnl,
      }
    : null;
  const scenarioPoint = Number.isFinite(Number(scenarioPrice))
    ? {
        underlying: Number(scenarioPrice),
        pnl: interpolateProfilePnl(profile.points, Number(scenarioPrice)),
      }
    : null;
  const currentLivePnl = profile.currentLivePnlBase != null ? Number(profile.currentLivePnlBase) : (currentPoint ? Number(currentPoint.pnl) : null);
  const expiryPnlAtCurrentUnderlying = profile.expiryPnlAtCurrentUnderlying != null ? Number(profile.expiryPnlAtCurrentUnderlying) : null;
  const showCurrentStateApproximation = profile.mode === 'today' && currentPoint && currentLivePnl != null;
  const currentLiveColor = currentLivePnl != null ? valueColor(currentLivePnl) : '#22c55e';
  const currentLiveStroke = currentLivePnl != null && currentLivePnl < 0 ? '#ef4444cc' : '#22c55ecc';
  const currentLiveBadgeFill = currentLivePnl != null && currentLivePnl < 0 ? '#2a1313' : '#0f2418';
  const currentLiveBadgeStroke = currentLivePnl != null && currentLivePnl < 0 ? '#ef444455' : '#22c55e55';
  const expiryAtCurrentColor = expiryPnlAtCurrentUnderlying != null ? valueColor(expiryPnlAtCurrentUnderlying) : COLORS.muted;
  const showZeroLabel = Math.abs(maxY) > 1e-9
    && Math.abs(minY) > 1e-9
    && Math.abs(zeroY - toY(maxY)) > 14
    && Math.abs(zeroY - toY(minY)) > 14;

  const handlePointerMove = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const relativeX = ((event.clientX - bounds.left) / Math.max(bounds.width, 1)) * width;
    const clamped = Math.max(paddingLeft, Math.min(width - paddingRight, relativeX));
    const ratio = (clamped - paddingLeft) / Math.max(chartWidth, 1);
    setHoveredIndex(Math.round(ratio * (profile.points.length - 1)));
  };

  const hoveredPoint = hoveredIndex != null ? profile.points[Math.max(0, Math.min(profile.points.length - 1, hoveredIndex))] : null;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: '240px', display: 'block' }} onMouseMove={handlePointerMove} onMouseLeave={() => setHoveredIndex(null)}>
      <defs>
        <linearGradient id={`${defsId}-positive`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#22c55e66" />
          <stop offset="100%" stopColor="#22c55e0d" />
        </linearGradient>
        <linearGradient id={`${defsId}-negative`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#ef444466" />
          <stop offset="100%" stopColor="#ef44440d" />
        </linearGradient>
        <clipPath id={`${defsId}-positive-clip`}>
          <rect x={paddingLeft} y={paddingTop} width={chartWidth} height={Math.max(0, zeroY - paddingTop)} />
        </clipPath>
        <clipPath id={`${defsId}-negative-clip`}>
          <rect x={paddingLeft} y={Math.max(paddingTop, zeroY)} width={chartWidth} height={Math.max(0, (height - paddingBottom) - zeroY)} />
        </clipPath>
      </defs>
      <rect x="0" y="0" width={width} height={height} fill="transparent" />
      <line x1={paddingLeft} y1={zeroY} x2={width - paddingRight} y2={zeroY} stroke="#ffffff22" strokeWidth="1" />
      <line x1={paddingLeft} y1={paddingTop} x2={paddingLeft} y2={height - paddingBottom} stroke="#ffffff18" strokeWidth="1" />
      <path d={areaPath} fill={`url(#${defsId}-positive)`} clipPath={`url(#${defsId}-positive-clip)`} />
      <path d={areaPath} fill={`url(#${defsId}-negative)`} clipPath={`url(#${defsId}-negative-clip)`} />
      {currentPoint ? (
        <>
          <line x1={toX(currentPoint.underlying)} y1={paddingTop} x2={toX(currentPoint.underlying)} y2={height - paddingBottom} stroke="#22c55e88" strokeDasharray="6 4" strokeWidth="1.5" />
          {showCurrentStateApproximation ? (
            <line x1={paddingLeft} y1={toY(currentLivePnl)} x2={width - paddingRight} y2={toY(currentLivePnl)} stroke={currentLiveStroke} strokeDasharray="6 4" strokeWidth="1.5" />
	          ) : null}
        </>
      ) : null}
      {scenarioPoint ? (
        <>
          <line x1={toX(scenarioPoint.underlying)} y1={paddingTop} x2={toX(scenarioPoint.underlying)} y2={height - paddingBottom} stroke="#a78bfa88" strokeDasharray="4 4" strokeWidth="1.25" />
          <circle cx={toX(scenarioPoint.underlying)} cy={toY(scenarioPoint.pnl)} r="4" fill="#a78bfa" stroke="#0f1d23" strokeWidth="1.5" />
        </>
      ) : null}
      {profile.strikes.map((strike) => (
        <line key={`strike-line-${strike}`} x1={toX(strike)} y1={paddingTop} x2={toX(strike)} y2={height - paddingBottom} stroke="#22d3ee33" strokeDasharray="4 4" strokeWidth="1" />
      ))}
      {profile.breakEvens.map((value) => (
        <line
          key={`be-line-${value}`}
          x1={toX(value)}
          y1={paddingTop}
          x2={toX(value)}
          y2={height - paddingBottom}
          stroke="#f59e0b66"
          strokeDasharray="2 6"
          strokeWidth="1"
        />
      ))}
      <path d={path} fill="none" stroke="#22d3ee" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {profile.strikes.map((strike, index) => (
        <g key={`strike-badge-${strike}`} transform={`translate(${Math.max(paddingLeft + 6, Math.min(width - paddingRight - 46, toX(strike) - 23))}, ${paddingTop + 8 + (index % 2 === 0 ? 0 : 22)})`}>
          <rect width="46" height="18" rx="9" fill="#0f1d23" stroke="#22d3ee33" />
          <text x="23" y="12.5" textAnchor="middle" fill="#67e8f9" fontSize="10" fontWeight="700">
            {Number(strike).toLocaleString('en-US', { maximumFractionDigits: 0 })}
          </text>
        </g>
      ))}
      {profile.breakEvens.map((value) => (
        <g key={`be-badge-${value}`} transform={`translate(${Math.max(paddingLeft + 6, Math.min(width - paddingRight - 70, toX(value) - 35))}, ${Math.min(height - paddingBottom - 24, zeroY + 36)})`}>
          <rect width="70" height="18" rx="9" fill="#2a1706" stroke="#f59e0b55" />
          <text x="35" y="12.5" textAnchor="middle" fill="#fbbf24" fontSize="10" fontWeight="700">
            BE {formatCompactNumberValue(value, 2)}
          </text>
        </g>
      ))}
      {currentPoint ? (
        <>
          {showCurrentStateApproximation ? (
            <g transform={`translate(${Math.max(paddingLeft + 8, width - paddingRight - 112)}, ${Math.max(paddingTop + 4, Math.min(height - paddingBottom - 22, toY(currentLivePnl) - 10))})`}>
              <rect width="104" height="20" rx="10" fill={currentLiveBadgeFill} stroke={currentLiveBadgeStroke} />
              <text x="52" y="13.5" textAnchor="middle" fill={currentLiveColor} fontSize="10.5" fontWeight="700">
                Jetzt {formatMoneyValue(currentLivePnl, currency)}
              </text>
            </g>
          ) : null}
          <g transform={`translate(${Math.max(paddingLeft + 8, Math.min(width - paddingRight - 132, toX(currentPoint.underlying) - 66))}, ${height - paddingBottom - 38})`}>
            <rect width="132" height="36" rx="10" fill="#0f2418" stroke="#22c55e44" />
            <text x="66" y="13" textAnchor="middle" fill="#4ade80" fontSize="10.5" fontWeight="700">
              Kurs {currentPoint.underlying.toLocaleString('en-US', { maximumFractionDigits: 2 })} {profile.priceCurrency || ''}
            </text>
            {expiryPnlAtCurrentUnderlying != null ? (
              <text x="66" y="27" textAnchor="middle" fill={expiryAtCurrentColor} fontSize="10.5" fontWeight="700">
                Verfall {formatMoneyValue(expiryPnlAtCurrentUnderlying, currency)}
              </text>
            ) : null}
          </g>
          <circle cx={toX(currentPoint.underlying)} cy={toY(currentPoint.pnl)} r="4" fill="#22c55e" stroke="#0f1d23" strokeWidth="1.5" />
        </>
      ) : null}
      {hoveredPoint ? (
        <>
          <line x1={toX(hoveredPoint.underlying)} y1={paddingTop} x2={toX(hoveredPoint.underlying)} y2={height - paddingBottom} stroke="#ffffff33" strokeDasharray="3 5" strokeWidth="1" />
          <circle cx={toX(hoveredPoint.underlying)} cy={toY(hoveredPoint.pnl)} r="4" fill="#22d3ee" />
          <g transform={`translate(${Math.min(width - 170, toX(hoveredPoint.underlying) + 10)}, ${Math.max(18, toY(hoveredPoint.pnl) - 42)})`}>
            <rect width="156" height="40" rx="8" fill="#09161b" stroke="#ffffff1a" />
            <text x="10" y="16" fill={COLORS.muted} fontSize="11">Underlying {hoveredPoint.underlying.toLocaleString('en-US', { maximumFractionDigits: 2 })} {profile.priceCurrency || ''}</text>
            <text x="10" y="31" fill={valueColor(hoveredPoint.pnl)} fontSize="12" fontWeight="700">P/L {formatMoneyValue(hoveredPoint.pnl, currency)}</text>
          </g>
        </>
      ) : null}
      <text x={paddingLeft - 8} y={toY(maxY)} textAnchor="end" fill={COLORS.muted} fontSize="11">{formatMoneyValue(maxY, currency)}</text>
      {showZeroLabel ? (
        <text x={paddingLeft - 8} y={zeroY + 4} textAnchor="end" fill={COLORS.muted} fontSize="11">{formatMoneyValue(0, currency)}</text>
      ) : null}
      <text x={paddingLeft - 8} y={toY(minY)} textAnchor="end" fill={COLORS.muted} fontSize="11">{formatMoneyValue(minY, currency)}</text>
      <text x={paddingLeft} y={height - 8} fill={COLORS.muted} fontSize="11">{profile.minPrice.toLocaleString('en-US', { maximumFractionDigits: 0 })}</text>
      <text x={width - paddingRight} y={height - 8} fill={COLORS.muted} fontSize="11" textAnchor="end">{profile.maxPrice.toLocaleString('en-US', { maximumFractionDigits: 0 })}</text>
    </svg>
  );
}

export default function AutomationSection({
  COLORS,
  isAdmin,
  tradeJournalOrderFormEnabled,
  onSessionExpired,
  onToast,
  tradeJournalAgentSecretSet,
  tradeJournalAgentName,
  tradeJournalAgentStatusLabel,
  tradeJournalAgentStatusColor,
  tradeJournalAgentLastSeenAt,
  tradeJournalAgentLastError,
  tradeJournalAgentLastErrorAt,
  tradeJournalAgentTwsConnected,
  tradeJournalAgentLastTwsConnectedAt,
  tradeJournalAgentLiveTwsConnected,
  tradeJournalAgentLiveLastTwsConnectedAt,
  tradeJournalAgentPaperTwsConnected,
  tradeJournalAgentPaperLastTwsConnectedAt,
  tradeJournalAgentLastQuoteDurationMs,
  onLiveAgentErrorCleared,
}) {
  const { Card, Badge, Btn } = useMemo(() => buildUiHelpers({ COLORS, th: {} }), [COLORS]);
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewRequests, setPreviewRequests] = useState([]);
  const [automationTrades, setAutomationTrades] = useState([]);
  const [automationSetups, setAutomationSetups] = useState([]);
  const [templateSetups, setTemplateSetups] = useState([]);
  const [automationSetupRuns, setAutomationSetupRuns] = useState([]);
  const [agentDownloadInfo, setAgentDownloadInfo] = useState({ available: false, download_url: '', file_name: '' });
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [selectedStrategyId, setSelectedStrategyId] = useState('');
  const [draftValues, setDraftValues] = useState({});
  const [setupName, setSetupName] = useState('');
  const [editingSetupId, setEditingSetupId] = useState(0);
  const [setupWeekday, setSetupWeekday] = useState(0);
  const [setupTimeLocal, setSetupTimeLocal] = useState('15:30');
  const [setupTimezone, setSetupTimezone] = useState('Europe/Berlin');
  const [setupMaxActiveTrades, setSetupMaxActiveTrades] = useState('1');
  const [setupAutoApprove, setSetupAutoApprove] = useState(false);
  const [setupEnabled, setSetupEnabled] = useState(true);
  const [setupIsTemplate, setSetupIsTemplate] = useState(false);
  const [setupSourceTemplateId, setSetupSourceTemplateId] = useState(0);
  const [setupSymbolFilter, setSetupSymbolFilter] = useState('ALLE');
  const [setupWeekdayFilter, setSetupWeekdayFilter] = useState('ALLE');
  const [setupSaving, setSetupSaving] = useState(false);
  const [runningSetupId, setRunningSetupId] = useState(0);
  const [deletingSetupId, setDeletingSetupId] = useState(0);
  const [orderSectionCollapsed, setOrderSectionCollapsed] = useState(true);
  const [orderProductType, setOrderProductType] = useState('option');
  const [stockDraft, setStockDraft] = useState({
    parent_trade_id: '',
    symbol: 'AAPL',
    exchange: 'SMART',
    currency: 'USD',
    primary_exchange: '',
    action: 'BUY',
    quantity: '1',
    order_type: 'MKT',
    limit_price: '',
    tws_target: 'paper',
    take_profit_percent: '',
    stop_loss_percent: '',
  });
  const [stockResolverStatus, setStockResolverStatus] = useState(null);
  const [stockResolverLoading, setStockResolverLoading] = useState(false);
  const [selectedStockContract, setSelectedStockContract] = useState(null);
  const [setupSectionCollapsed, setSetupSectionCollapsed] = useState(true);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestMessage, setRequestMessage] = useState('');
  const [requestError, setRequestError] = useState('');
  const [cancelingRequestId, setCancelingRequestId] = useState('');
  const [approvingRequestId, setApprovingRequestId] = useState('');
  const [clearingHistory, setClearingHistory] = useState(false);
  const [clearingAgentError, setClearingAgentError] = useState(false);
  const [closingTradeId, setClosingTradeId] = useState(0);
  const [closeTradeDialog, setCloseTradeDialog] = useState(null);
  const [deletingTradeId, setDeletingTradeId] = useState(0);
  const [editingTradeId, setEditingTradeId] = useState(0);
  const [savingExitTradeId, setSavingExitTradeId] = useState(0);
  const [exitDrafts, setExitDrafts] = useState({});
  const [expandedTradeIds, setExpandedTradeIds] = useState({});
  const [optionProfileModes, setOptionProfileModes] = useState({});
  const [optionProfileScenarioPrices, setOptionProfileScenarioPrices] = useState({});
  const [tradeSort, setTradeSort] = useState({ key: 'openAt', direction: 'desc' });
  const canUseOrderForm = !!isAdmin || tradeJournalOrderFormEnabled !== false;
  const hasRestrictedTemplateContext = !canUseOrderForm && Number(setupSourceTemplateId) > 0;

  const parseApiDate = (value) => {
    if (!value) return null;
    try {
      const normalizedValue = typeof value === 'string' && !/[zZ]|[+-]\d{2}:\d{2}$/.test(value)
        ? `${value}Z`
        : value;
      const parsed = new Date(normalizedValue);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    } catch {
      return null;
    }
  };

  const formatRelativeAge = (value) => {
    const parsed = parseApiDate(value);
    if (!parsed) return '';
    const ageSeconds = Math.max(0, Math.floor((Date.now() - parsed.getTime()) / 1000));
    if (ageSeconds < 10) return 'gerade eben';
    if (ageSeconds < 60) return `vor ${ageSeconds}s`;
    const ageMinutes = Math.floor(ageSeconds / 60);
    if (ageMinutes < 60) return `vor ${ageMinutes}m`;
    const ageHours = Math.floor(ageMinutes / 60);
    if (ageHours < 48) return `vor ${ageHours}h`;
    return `vor ${Math.floor(ageHours / 24)}d`;
  };

  const formatDurationMs = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric < 0) return '—';
    if (numeric < 1000) return `${Math.round(numeric)} ms`;
    return `${(numeric / 1000).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} s`;
  };

  const formatDateTime = (value) => {
    const parsed = parseApiDate(value);
    if (!parsed) return '';
    return parsed.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const loadPreviewRequests = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setRequestsLoading(true);
    try {
      const data = await apiRequest('/trade-journal/live-agent/order-preview/requests');
      setPreviewRequests(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired?.();
        return;
      }
      if (!silent) {
        setRequestError(err instanceof ApiError ? err.message : 'Preview-Verlauf konnte nicht geladen werden.');
      }
    } finally {
      if (!silent) setRequestsLoading(false);
    }
  }, [onSessionExpired]);

  const loadAutomationTrades = useCallback(async ({ silent = false } = {}) => {
    try {
      const data = await apiRequest('/trade-journal/live-agent/automation-trades');
      setAutomationTrades(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired?.();
      } else if (!silent) {
        setRequestError(err instanceof ApiError ? err.message : 'Automation-Trades konnten nicht geladen werden.');
      }
    }
  }, [onSessionExpired]);

  const loadAutomationSetups = useCallback(async ({ silent = false } = {}) => {
    try {
      const [setupData, runData] = await Promise.all([
        apiRequest('/trade-journal/live-agent/automation-setups'),
        apiRequest('/trade-journal/live-agent/automation-setups/runs?limit=5'),
      ]);
      setAutomationSetups(Array.isArray(setupData?.items) ? setupData.items : []);
      setTemplateSetups(Array.isArray(setupData?.templates) ? setupData.templates : []);
      setAutomationSetupRuns(Array.isArray(runData?.items) ? runData.items : []);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired?.();
      } else if (!silent) {
        setRequestError(err instanceof ApiError ? err.message : 'Automation-Setups konnten nicht geladen werden.');
      }
    }
  }, [onSessionExpired]);

  const loadAgentDownloadInfo = useCallback(async () => {
    try {
      const data = await apiRequest('/trade-journal/live-agent/download-info');
      setAgentDownloadInfo(data && typeof data === 'object' ? data : { available: false });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired?.();
        return;
      }
      setAgentDownloadInfo({ available: false, download_url: '', file_name: '' });
    }
  }, [onSessionExpired]);

  useEffect(() => {
    let cancelled = false;
    async function loadCapabilities() {
      setLoading(true);
      setError('');
      try {
        const [capabilityData, requestData, tradeData, setupData, runData, downloadData] = await Promise.all([
          apiRequest('/trade-journal/live-agent/order-preview/capabilities'),
          apiRequest('/trade-journal/live-agent/order-preview/requests'),
          apiRequest('/trade-journal/live-agent/automation-trades'),
          apiRequest('/trade-journal/live-agent/automation-setups'),
          apiRequest('/trade-journal/live-agent/automation-setups/runs?limit=5'),
          apiRequest('/trade-journal/live-agent/download-info'),
        ]);
        if (cancelled) return;
        setCapabilities(capabilityData);
        setPreviewRequests(Array.isArray(requestData?.items) ? requestData.items : []);
        setAutomationTrades(Array.isArray(tradeData?.items) ? tradeData.items : []);
        setAutomationSetups(Array.isArray(setupData?.items) ? setupData.items : []);
        setTemplateSetups(Array.isArray(setupData?.templates) ? setupData.templates : []);
        setAutomationSetupRuns(Array.isArray(runData?.items) ? runData.items : []);
        setAgentDownloadInfo(downloadData && typeof downloadData === 'object' ? downloadData : { available: false });
        const firstStrategy = Array.isArray(capabilityData?.strategies) ? capabilityData.strategies[0] : null;
        if (firstStrategy) {
          setSelectedStrategyId((current) => current || firstStrategy.key);
          setDraftValues((current) => {
            if (Object.keys(current).length > 0) return current;
            return Object.fromEntries((firstStrategy.fields || []).map((field) => [field.key, fieldInitialValue(field)]));
          });
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onSessionExpired?.();
          return;
        }
        setError(err instanceof ApiError ? err.message : 'Automation konnte nicht geladen werden.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadCapabilities();
    return () => {
      cancelled = true;
    };
  }, [onSessionExpired]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      loadPreviewRequests({ silent: true });
      loadAutomationTrades({ silent: true });
      loadAutomationSetups({ silent: true });
      loadAgentDownloadInfo();
    }, 10000);
    return () => window.clearInterval(intervalId);
  }, [loadPreviewRequests, loadAutomationTrades, loadAutomationSetups, loadAgentDownloadInfo]);

  useEffect(() => {
    const requestId = String(stockResolverStatus?.request_id || '').trim();
    const status = String(stockResolverStatus?.status || '').trim().toLowerCase();
    if (!requestId || !['pending', 'processing'].includes(status)) return undefined;
    const intervalId = window.setInterval(async () => {
      try {
        const data = await apiRequest(`/trade-journal/live-agent/stock-resolver/status?request_id=${encodeURIComponent(requestId)}`);
        setStockResolverStatus(data);
      } catch {
        // Der nächste manuelle Klick kann den Status erneut laden.
      }
    }, 3000);
    return () => window.clearInterval(intervalId);
  }, [stockResolverStatus?.request_id, stockResolverStatus?.status]);

  useEffect(() => {
    const status = String(stockResolverStatus?.status || '').trim().toLowerCase();
    if (status !== 'done') return;
    const matches = Array.isArray(stockResolverStatus?.result?.matches)
      ? stockResolverStatus.result.matches
      : [];
    if (!matches.length) return;
    const currentConId = Number(selectedStockContract?.con_id || 0);
    const match = matches.find((item) => Number(item?.con_id || 0) === currentConId) || matches[0];
    if (!match) return;
    const nextLimitPrice = chooseStockLimitPriceFromQuote(match, stockDraft.action);
    setSelectedStockContract((current) => ({ ...(current || {}), ...match }));
    setStockDraft((current) => ({
      ...current,
      symbol: match.symbol || current.symbol,
      exchange: match.exchange || current.exchange,
      currency: match.currency || current.currency,
      primary_exchange: match.primary_exchange || current.primary_exchange || '',
      limit_price: nextLimitPrice || current.limit_price,
    }));
  }, [
    selectedStockContract?.con_id,
    stockDraft.action,
    stockResolverStatus?.request_id,
    stockResolverStatus?.result?.matches,
    stockResolverStatus?.status,
  ]);

  const selectedStrategy = useMemo(
    () => capabilities?.strategies?.find((item) => item.key === selectedStrategyId) || null,
    [capabilities, selectedStrategyId],
  );

  const selectedStrategyIsLongOption = useMemo(
    () => isLongOptionDraft(selectedStrategy?.key, draftValues),
    [selectedStrategy?.key, draftValues],
  );

  const visibleFields = useMemo(() => {
    const fields = selectedStrategy?.fields || [];
    const strikeMode = String(draftValues.strike_mode || 'delta').trim().toLowerCase();
    const strategyKey = String(selectedStrategy?.key || '').trim().toLowerCase();
    const deltaFieldKey = strategyKey === 'bull_put_spread'
      ? 'short_put_delta'
      : strategyKey === 'bear_call_spread'
        ? 'short_call_delta'
        : 'target_delta';

    const filteredFields = fields.filter((field) => {
      if (field.key === deltaFieldKey) return strikeMode === 'delta';
      if (field.key === 'target_delta' && deltaFieldKey !== 'target_delta') return false;
      if ((field.key === 'short_put_delta' || field.key === 'short_call_delta') && field.key !== deltaFieldKey) return false;
      if (field.key === 'strike') return strikeMode === 'absolute';
      if (field.key === 'target_option_price') return strikeMode === 'premium';
      if (field.key === 'target_otm_percent') return strikeMode === 'otm_percent';
      if (field.key === 'target_atm_offset') return strikeMode === 'atm_offset';
      return true;
    });

    const preferredOrder = [
      'underlying_symbol',
      'tws_target',
      'expiry_mode',
      'days_to_expiry',
      'expiry_date',
      'quantity',
      'right',
      'direction',
      'strike_mode',
      'target_delta',
      'short_put_delta',
      'short_call_delta',
      'strike',
      'target_option_price',
      'target_otm_percent',
      'target_atm_offset',
      'spread_width',
      'selection_mode',
      'use_exact_dte',
      'take_profit_percent',
      'stop_loss_percent',
      'exit_attempts',
      'exit_wait_seconds',
      'exit_adjustment',
      'exit_market_on_last_attempt',
    ];

    return [...filteredFields].sort((a, b) => {
      const aIndex = preferredOrder.indexOf(a.key);
      const bIndex = preferredOrder.indexOf(b.key);
      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    });
  }, [selectedStrategy, draftValues.strike_mode]);

  const previewButtonLabel = useMemo(() => {
    const twsTargetField = (selectedStrategy?.fields || []).find((field) => field.key === 'tws_target');
    const twsTargetValue = String(draftValues.tws_target || '').trim().toLowerCase();
    const selectedTargetOption = Array.isArray(twsTargetField?.options)
      ? twsTargetField.options.find((option) => String(option.value || '').trim().toLowerCase() === twsTargetValue)
      : null;
    const targetLabel = String(selectedTargetOption?.label || '').trim().toLowerCase();

    if (targetLabel.includes('paper') || twsTargetValue.includes('paper')) return 'Paper Trade prüfen';
    if (targetLabel.includes('live') || twsTargetValue.includes('live')) return 'Live Trade prüfen';
    return 'Trade prüfen';
  }, [draftValues.tws_target, selectedStrategy]);

  const stockTwsTargetLabel = useMemo(() => (
    String(stockDraft.tws_target || '').trim().toLowerCase() === 'live' ? 'Live' : 'Paper'
  ), [stockDraft.tws_target]);
  const stockEditingTradeId = Number(stockDraft.parent_trade_id || 0);
  const stockIsEditingPosition = Number.isFinite(stockEditingTradeId) && stockEditingTradeId > 0;

  useEffect(() => {
    if (!selectedStrategy) return;
    setDraftValues((current) => {
      const next = { ...current };
      for (const field of selectedStrategy.fields || []) {
        if (next[field.key] === undefined) {
          next[field.key] = fieldInitialValue(field);
        }
      }
      return next;
    });
  }, [selectedStrategy]);

  useEffect(() => {
    if (!selectedStrategy) return;
    const underlyingSymbol = String(draftValues.underlying_symbol || '').trim().toUpperCase();
    if (!underlyingSymbol) return;
    const desiredWaitSeconds = defaultExitWaitSecondsForSymbol(underlyingSymbol);
    setDraftValues((current) => {
      const currentValue = String(current.exit_wait_seconds ?? '').trim();
      if (currentValue && currentValue !== '5' && currentValue !== '30') return current;
      if (currentValue === desiredWaitSeconds) return current;
      return { ...current, exit_wait_seconds: desiredWaitSeconds };
    });
  }, [draftValues.underlying_symbol, selectedStrategy]);

  const renderField = (field) => {
    const value = draftValues[field.key] ?? '';
    const selectedOption = Array.isArray(field.options)
      ? field.options.find((option) => String(option.value) === String(value))
      : null;
    const helpText = [field.description, selectedOption?.description]
      .filter((item) => String(item || '').trim())
      .join('\n\n');
    const commonStyle = {
      width: '100%',
      borderRadius: '12px',
      border: `1px solid ${COLORS.border}`,
      padding: '11px 12px',
      fontSize: '14px',
      boxSizing: 'border-box',
      ...AUTOMATION_FIELD_STYLE,
    };

    const longOptionStopLossError = field.key === 'stop_loss_percent'
      ? getLongOptionStopLossError(String(value || '').trim().replace(',', '.'), selectedStrategyIsLongOption)
      : '';
    return (
      <div>
        {field.input_type === 'select' ? (
          <select
            value={value}
            disabled={!canUseOrderForm}
            onChange={(event) => setDraftValues((current) => ({ ...current, [field.key]: event.target.value }))}
            style={{ ...commonStyle, ...AUTOMATION_SELECT_STYLE }}
            title={helpText || undefined}
          >
            {(field.options || []).map((option) => (
              <option
                key={option.value}
                value={option.value}
                title={option.description || option.label}
                style={AUTOMATION_SELECT_OPTION_STYLE}
              >
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={(field.input_type === 'integer' || field.input_type === 'decimal') ? 'number' : field.input_type === 'date' ? 'date' : 'text'}
            step={field.input_type === 'integer' ? '1' : field.input_type === 'decimal' ? 'any' : undefined}
            value={value}
            disabled={!canUseOrderForm}
            onChange={(event) => {
              const nextValue = field.input_type === 'decimal'
                ? String(event.target.value || '').replace(',', '.')
                : event.target.value;
              setDraftValues((current) => ({ ...current, [field.key]: nextValue }));
              if (field.key === 'stop_loss_percent' && selectedStrategyIsLongOption) {
                const nextNumeric = nextValue ? Number(nextValue) : null;
                const nextError = getLongOptionStopLossError(nextNumeric, true);
                setRequestError((current) => {
                  if (nextError) return nextError;
                  return current === LONG_OPTION_STOP_LOSS_INFO ? '' : current;
                });
              }
            }}
            placeholder={field.placeholder || field.default_value || ''}
            style={commonStyle}
            title={helpText || undefined}
          />
        )}
        {longOptionStopLossError ? (
          <div style={{ color: longOptionStopLossError ? COLORS.red : COLORS.muted, fontSize: '11px', lineHeight: 1.5, marginTop: '6px' }}>
            {longOptionStopLossError}
          </div>
        ) : null}
      </div>
    );
  };

  const renderStockField = (key, label, inputType = 'text', options = []) => {
    const value = stockDraft[key] ?? '';
    const updateValue = (nextValue) => {
      if (['symbol', 'exchange', 'currency', 'primary_exchange', 'tws_target'].includes(key)) {
        setSelectedStockContract(null);
      }
      setStockDraft((current) => ({ ...current, [key]: nextValue }));
    };
    const lockedForPositionEdit = stockIsEditingPosition
      && ['symbol', 'exchange', 'currency', 'primary_exchange', 'tws_target'].includes(key);
    const commonStyle = {
      width: '100%',
      borderRadius: '12px',
      border: `1px solid ${COLORS.border}`,
      padding: '11px 12px',
      fontSize: '14px',
      boxSizing: 'border-box',
      ...AUTOMATION_FIELD_STYLE,
    };
    return (
      <div>
        <label style={{ display: 'block', fontSize: '12px', color: COLORS.muted, marginBottom: '6px' }}>
          {label}
        </label>
        {inputType === 'select' ? (
          <select
            value={value}
            disabled={!canUseOrderForm || lockedForPositionEdit}
            onChange={(event) => updateValue(event.target.value)}
            style={{ ...commonStyle, ...AUTOMATION_SELECT_STYLE }}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value} style={AUTOMATION_SELECT_OPTION_STYLE}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={inputType}
            step={inputType === 'number' ? 'any' : undefined}
            value={value}
            disabled={!canUseOrderForm || lockedForPositionEdit}
            onChange={(event) => updateValue(event.target.value)}
            style={commonStyle}
          />
        )}
      </div>
    );
  };

  const agentIsCurrentlyConnected =
    tradeJournalAgentStatusLabel === 'Verbunden' || tradeJournalAgentStatusColor === 'green';
  const renderTargetTwsBadge = (label, connected, lastConnectedAt) => {
    if (connected === true && agentIsCurrentlyConnected) {
      return <Badge color="green">{`${label} verbunden`}</Badge>;
    }
    if (lastConnectedAt) {
      return (
        <Badge color={connected === false ? 'red' : 'ghost'}>
          {connected === false ? `${label} zuletzt ok ${formatRelativeAge(lastConnectedAt)}` : `${label} ok ${formatRelativeAge(lastConnectedAt)}`}
        </Badge>
      );
    }
    if (connected === false) {
      return <Badge color="red">{`${label} getrennt`}</Badge>;
    }
    return null;
  };

  const submitPreviewRequest = async () => {
    if (!selectedStrategy) return;
    if (!canUseOrderForm) {
      const message = 'Diese Option steht aktuell noch nicht zur Verfügung.';
      setRequestError(message);
      setRequestMessage('');
      onToast?.(message, 'error');
      return;
    }
    if (!agentIsCurrentlyConnected) {
      const message = 'Agent nicht verbunden.';
      setRequestError(message);
      setRequestMessage('');
      onToast?.(message, 'error');
      return;
    }
    const normalizedStopLossText = String(draftValues.stop_loss_percent || '').trim().replace(',', '.');
    const normalizedStopLossValue = normalizedStopLossText ? Number(normalizedStopLossText) : null;
    const longOptionStopLossError = getLongOptionStopLossError(normalizedStopLossValue, selectedStrategyIsLongOption);
    if (longOptionStopLossError) {
      setRequestError(longOptionStopLossError);
      setRequestMessage('');
      return;
    }
    setRequestLoading(true);
    setRequestError('');
    setRequestMessage('');
    try {
      const payload = {
        strategy_key: selectedStrategy.key,
        field_values: (selectedStrategy.fields || []).map((field) => ({
          key: field.key,
          value: String(draftValues[field.key] ?? '').trim(),
        })),
      };
      const data = await apiRequest('/trade-journal/live-agent/order-preview/request', {
        method: 'POST',
        body: payload,
      });
      const message = `Preview-Request erstellt (${data.request_id}).`;
      setRequestMessage(message);
      await loadPreviewRequests({ silent: true });
      onToast?.(message, 'success');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired?.();
        return;
      }
      const message = err instanceof ApiError ? err.message : 'Preview-Request konnte nicht erstellt werden.';
      setRequestError(message);
    } finally {
      setRequestLoading(false);
    }
  };

  const submitStockResolverRequest = async (options = {}) => {
    const { silent = false, keepSelection = false, draftOverride = null } = options || {};
    if (stockResolverLoading) return;
    if (!agentIsCurrentlyConnected) {
      const message = 'Agent nicht verbunden.';
      setRequestError(message);
      if (!silent) onToast?.(message, 'error');
      return;
    }
    const sourceDraft = draftOverride || stockDraft;
    const symbol = String(sourceDraft.symbol || '').trim().toUpperCase();
    if (!symbol) {
      setRequestError('Bitte zuerst ein Aktien-Symbol eingeben.');
      return;
    }
    setStockResolverLoading(true);
    if (!keepSelection) setSelectedStockContract(null);
    setRequestError('');
    if (!silent) setRequestMessage('');
    try {
      const request = await apiRequest('/trade-journal/live-agent/stock-resolver/request', {
        method: 'POST',
        body: {
          symbol,
          exchange: String(sourceDraft.exchange || 'SMART').trim().toUpperCase() || 'SMART',
          currency: String(sourceDraft.currency || 'USD').trim().toUpperCase() || 'USD',
          primary_exchange: String(sourceDraft.primary_exchange || '').trim().toUpperCase() || null,
          tws_target: String(sourceDraft.tws_target || 'paper').trim().toLowerCase() || 'paper',
        },
      });
      setStockResolverStatus(request);
      if (!silent) onToast?.('Ticker-Suche an den Agenten übergeben.', 'success');
      window.setTimeout(async () => {
        try {
          const status = await apiRequest(`/trade-journal/live-agent/stock-resolver/status?request_id=${encodeURIComponent(request.request_id || '')}`);
          setStockResolverStatus(status);
        } catch {
          // Der Status-Poller übernimmt danach.
        }
      }, 2500);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired?.();
        return;
      }
      const message = err instanceof ApiError ? err.message : 'Ticker-Suche konnte nicht gestartet werden.';
      setRequestError(message);
      if (!silent) onToast?.(message, 'error');
    } finally {
      setStockResolverLoading(false);
    }
  };

  const submitStockPreviewRequest = async () => {
    if (!canUseOrderForm) {
      const message = 'Diese Option steht aktuell noch nicht zur Verfügung.';
      setRequestError(message);
      setRequestMessage('');
      onToast?.(message, 'error');
      return;
    }
    if (!agentIsCurrentlyConnected) {
      const message = 'Agent nicht verbunden.';
      setRequestError(message);
      setRequestMessage('');
      onToast?.(message, 'error');
      return;
    }
    const symbol = String(stockDraft.symbol || '').trim().toUpperCase();
    const quantity = Number(String(stockDraft.quantity || '').replace(',', '.'));
    const orderType = String(stockDraft.order_type || 'MKT').trim().toUpperCase();
    const limitPriceText = String(stockDraft.limit_price || '').trim().replace(',', '.');
    const limitPrice = limitPriceText ? Number(limitPriceText) : null;
    const parseOptionalPercent = (value, label) => {
      const text = String(value ?? '').trim().replace(',', '.');
      if (!text) return null;
      const numeric = Number(text);
      if (!Number.isFinite(numeric) || numeric < 0) {
        throw new Error(`Bitte einen gültigen Wert für ${label} eingeben.`);
      }
      return numeric;
    };
    if (!symbol) {
      setRequestError('Bitte ein Aktien-Symbol eingeben.');
      return;
    }
    if (!selectedStockContract) {
      setRequestError('Bitte zuerst Ticker suchen und einen Contract auswählen.');
      return;
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setRequestError('Bitte eine gültige Aktien-Menge eingeben.');
      return;
    }
    if (orderType === 'LMT' && (!Number.isFinite(limitPrice) || limitPrice <= 0)) {
      setRequestError('Bitte einen gültigen Limitpreis eingeben.');
      return;
    }
    setRequestLoading(true);
    setRequestError('');
    setRequestMessage('');
    try {
      const wasEditingStockPosition = stockEditingTradeId > 0;
      const takeProfitPercent = parseOptionalPercent(stockDraft.take_profit_percent, 'TP %');
      const stopLossPercent = parseOptionalPercent(stockDraft.stop_loss_percent, 'SL %');
      const data = await apiRequest('/trade-journal/live-agent/automation-trades/stock-order', {
        method: 'POST',
        body: {
          parent_trade_id: stockEditingTradeId || null,
          symbol,
          exchange: String(stockDraft.exchange || 'SMART').trim().toUpperCase() || 'SMART',
          currency: String(stockDraft.currency || 'USD').trim().toUpperCase() || 'USD',
          primary_exchange: String(stockDraft.primary_exchange || '').trim().toUpperCase() || null,
          action: String(stockDraft.action || 'BUY').trim().toUpperCase(),
          quantity,
          order_type: orderType,
          limit_price: orderType === 'LMT' ? limitPrice : null,
          tws_target: String(stockDraft.tws_target || 'paper').trim().toLowerCase() || 'paper',
          contract: selectedStockContract,
          take_profit_percent: takeProfitPercent,
          stop_loss_percent: stopLossPercent,
        },
      });
      const message = wasEditingStockPosition
        ? `Aktien-Order für ${symbol} geprüft. Bitte im Verlauf freigeben.`
        : `Aktien-Trade geprüft (${data.request_id || symbol}). Bitte im Verlauf freigeben.`;
      if (wasEditingStockPosition) {
        setStockDraft((current) => ({
          ...current,
          parent_trade_id: '',
          action: 'BUY',
          quantity: '1',
          order_type: 'MKT',
          limit_price: '',
          take_profit_percent: '',
          stop_loss_percent: '',
        }));
        setSelectedStockContract(null);
        setStockResolverStatus(null);
      }
      setRequestMessage(wasEditingStockPosition ? '' : message);
      await loadPreviewRequests({ silent: true });
      onToast?.(message, 'success');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired?.();
        return;
      }
      const message = err instanceof ApiError ? err.message : err.message || 'Aktien-Trade konnte nicht geprüft werden.';
      setRequestError(message);
      onToast?.(message, 'error');
    } finally {
      setRequestLoading(false);
    }
  };

  const saveCurrentDraftAsSetup = async () => {
    if (!selectedStrategy) return;
    if (!canUseOrderForm && !hasRestrictedTemplateContext) {
      const message = 'Bitte zuerst eine Vorlage laden.';
      setRequestError(message);
      setRequestMessage('');
      onToast?.(message, 'error');
      return;
    }
    const trimmedName = String(setupName || '').trim();
    if (!trimmedName && canUseOrderForm) {
      setRequestError('Bitte zuerst einen Setup-Namen eingeben.');
      return;
    }
    setSetupSaving(true);
    setRequestError('');
    setRequestMessage('');
    try {
      const displayName = trimmedName || String(setupName || '').trim() || 'Vorlage';
      const body = {
        name: displayName,
        strategy_key: selectedStrategy.key,
        field_values: (selectedStrategy.fields || []).map((field) => ({
          key: field.key,
          value: String(draftValues[field.key] ?? '').trim(),
        })),
        source_template_id: hasRestrictedTemplateContext ? Number(setupSourceTemplateId) : null,
        is_template: !!setupIsTemplate,
        enabled: setupEnabled,
        auto_approve: setupAutoApprove,
        max_active_trades: Number(setupMaxActiveTrades || 1),
        schedule: {
          weekday: Number(setupWeekday || 0),
          time_local: setupTimeLocal || '15:30',
          timezone: setupTimezone || 'Europe/Berlin',
          enabled: true,
        },
      };
      const isEditing = Number(editingSetupId) > 0;
      await apiRequest(
        isEditing
          ? `/trade-journal/live-agent/automation-setups/${editingSetupId}`
          : '/trade-journal/live-agent/automation-setups',
        {
          method: isEditing ? 'PATCH' : 'POST',
          body,
        },
      );
      await loadAutomationSetups({ silent: true });
      const entityLabel = hasRestrictedTemplateContext ? 'Vorlagen-Setup' : (setupIsTemplate ? 'Vorlage' : 'Setup');
      setRequestMessage(isEditing ? `${entityLabel} '${displayName}' aktualisiert.` : `${entityLabel} '${displayName}' gespeichert.`);
      onToast?.(isEditing ? `${entityLabel} '${displayName}' aktualisiert.` : `${entityLabel} '${displayName}' gespeichert.`, 'success');
      setEditingSetupId(0);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired?.();
        return;
      }
      const message = err instanceof ApiError ? err.message : `Setup konnte nicht ${Number(editingSetupId) > 0 ? 'aktualisiert' : 'gespeichert'} werden.`;
      setRequestError(message);
      onToast?.(message, 'error');
    } finally {
      setSetupSaving(false);
    }
  };

  const resetSetupForm = () => {
    setEditingSetupId(0);
    setSetupName('');
    setSetupWeekday(0);
    setSetupTimeLocal('15:30');
    setSetupTimezone('Europe/Berlin');
    setSetupMaxActiveTrades('1');
    setSetupAutoApprove(false);
    setSetupEnabled(true);
    setSetupIsTemplate(false);
    setSetupSourceTemplateId(0);
  };

  const startEditSetup = (setup) => {
    if (!setup) return;
    const sourceTemplateId = Number(setup.source_template_id || 0);
    const templateSetup = setup.is_template
      ? setup
      : (sourceTemplateId > 0 ? (templateSetups.find((item) => Number(item.id) === sourceTemplateId) || null) : null);
    const personalizedTemplateSetup = !canUseOrderForm && setup.is_template
      ? (automationSetups.find((item) => Number(item.source_template_id || 0) === Number(setup.id)) || null)
      : null;
    const effectiveOrderSetup = !canUseOrderForm && templateSetup ? templateSetup : setup;
    const effectiveSettingsSetup = !canUseOrderForm && setup.is_template ? (personalizedTemplateSetup || setup) : setup;
    setEditingSetupId(!canUseOrderForm && setup.is_template ? (personalizedTemplateSetup ? Number(personalizedTemplateSetup.id) : 0) : (Number(setup.id) || 0));
    setSelectedStrategyId(effectiveOrderSetup.strategy_key);
    setDraftValues({ ...(effectiveOrderSetup.field_values || {}) });
    setSetupName(String((!canUseOrderForm && templateSetup ? templateSetup.name : effectiveSettingsSetup.name) || ''));
    setSetupWeekday(Number(effectiveSettingsSetup.schedule?.weekday ?? 0));
    setSetupTimeLocal(String(effectiveSettingsSetup.schedule?.time_local || '15:30'));
    setSetupTimezone(String(effectiveSettingsSetup.schedule?.timezone || 'Europe/Berlin'));
    setSetupMaxActiveTrades(String(effectiveSettingsSetup.max_active_trades || 1));
    setSetupAutoApprove(Boolean(effectiveSettingsSetup.auto_approve));
    setSetupEnabled(Boolean(effectiveSettingsSetup.enabled));
    setSetupIsTemplate(Boolean(canUseOrderForm && setup.is_template));
    setSetupSourceTemplateId(!canUseOrderForm ? Number((templateSetup || (setup.is_template ? setup : null))?.id || sourceTemplateId || 0) : sourceTemplateId);
    setRequestMessage(`${setup.is_template ? 'Vorlage' : 'Setup'} '${setup.name}' wird bearbeitet.`);
    setRequestError('');
  };

  const loadSetupIntoForm = (setup) => {
    startEditSetup(setup);
    setRequestMessage(`${setup.is_template ? 'Vorlage' : 'Setup'} '${setup.name}' in die Maske geladen.`);
  };

  const runSetupNow = async (setupId) => {
    if (!setupId) return;
    if (!agentIsCurrentlyConnected) {
      const message = 'Agent nicht verbunden.';
      setRequestError(message);
      onToast?.(message, 'error');
      return;
    }
    setRunningSetupId(setupId);
    setRequestError('');
    try {
      const data = await apiRequest(`/trade-journal/live-agent/automation-setups/${setupId}/run-now`, { method: 'POST' });
      await Promise.all([
        loadAutomationSetups({ silent: true }),
        loadPreviewRequests({ silent: true }),
      ]);
      onToast?.(`Setup gestartet (${data.status}).`, 'success');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired?.();
        return;
      }
      const message = err instanceof ApiError ? err.message : 'Setup konnte nicht gestartet werden.';
      setRequestError(message);
      onToast?.(message, 'error');
    } finally {
      setRunningSetupId(0);
    }
  };

  const deleteSetup = async (setupId) => {
    if (!setupId || !window.confirm('Dieses Setup wirklich löschen?')) return;
    setDeletingSetupId(setupId);
    setRequestError('');
    try {
      await apiRequest(`/trade-journal/live-agent/automation-setups/${setupId}`, { method: 'DELETE' });
      await loadAutomationSetups({ silent: true });
      onToast?.('Setup gelöscht.', 'success');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired?.();
        return;
      }
      const message = err instanceof ApiError ? err.message : 'Setup konnte nicht gelöscht werden.';
      setRequestError(message);
      onToast?.(message, 'error');
    } finally {
      setDeletingSetupId(0);
    }
  };

  const renderSetupTable = (rows, { templates = false } = {}) => {
    if (!rows.length) {
      return (
        <div style={{ color: COLORS.muted, fontSize: '13px' }}>
          {templates ? 'Keine Vorlagen für die aktuellen Filter vorhanden.' : 'Keine Setups für die aktuellen Filter vorhanden.'}
        </div>
      );
    }
    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: templates ? '1160px' : '1080px' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
              <th style={{ textAlign: 'left', padding: '10px 12px', color: COLORS.muted, fontSize: '12px' }}>Name</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', color: COLORS.muted, fontSize: '12px' }}>Setup</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', color: COLORS.muted, fontSize: '12px' }}>Symbol</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', color: COLORS.muted, fontSize: '12px' }}>Wochentag</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', color: COLORS.muted, fontSize: '12px' }}>Uhrzeit</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', color: COLORS.muted, fontSize: '12px' }}>Max aktiv</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', color: COLORS.muted, fontSize: '12px' }}>Status</th>
              {templates ? <th style={{ textAlign: 'left', padding: '10px 12px', color: COLORS.muted, fontSize: '12px' }}>Von</th> : null}
              <th style={{ textAlign: 'right', padding: '10px 12px', color: COLORS.muted, fontSize: '12px' }}>Aktion</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((setup) => {
              const isDerivedTemplateSetup = Number(setup.source_template_id || 0) > 0;
              const canEditSetup = templates ? (isAdmin || !canUseOrderForm) : (canUseOrderForm || isDerivedTemplateSetup);
              const canLoadSetup = templates ? true : (canUseOrderForm || isDerivedTemplateSetup);
              const canDeleteSetup = templates ? isAdmin : (canUseOrderForm || isDerivedTemplateSetup);
              return (
                <tr key={`${templates ? 'tpl' : 'own'}-${setup.id}`} style={{ borderBottom: `1px solid ${COLORS.border}55` }}>
                  <td style={{ padding: '12px', color: COLORS.text, fontSize: '13px', fontWeight: '700' }}>{setup.name}</td>
                  <td style={{ padding: '12px', color: COLORS.text, fontSize: '13px' }}>{formatStrategyTitle(setup.strategy_key)}</td>
                  <td style={{ padding: '12px', color: COLORS.text, fontSize: '13px' }}>{String(setup.field_values?.underlying_symbol || '—').toUpperCase()}</td>
                  <td style={{ padding: '12px', color: COLORS.text, fontSize: '13px' }}>{getAutomationWeekdayLabel(setup.schedule?.weekday)}</td>
                  <td style={{ padding: '12px', color: COLORS.text, fontSize: '13px' }}>{setup.schedule?.time_local || '—'}</td>
                  <td style={{ padding: '12px', color: COLORS.text, fontSize: '13px' }}>{setup.max_active_trades}</td>
                  <td style={{ padding: '12px', color: COLORS.text, fontSize: '13px' }}>
                    <div>{setup.enabled ? 'Aktiv' : 'Pausiert'}</div>
                    <div style={{ color: COLORS.muted, fontSize: '11px', marginTop: '4px' }}>{setup.auto_approve ? 'Auto-Freigabe an' : 'Auto-Freigabe aus'}</div>
                  </td>
                  {templates ? (
                    <td style={{ padding: '12px', color: COLORS.text, fontSize: '13px' }}>{setup.owner_email || 'Admin'}</td>
                  ) : null}
                  <td style={{ padding: '12px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
                      {canEditSetup ? (
                        <Btn color="ghost" size="sm" onClick={() => startEditSetup(setup)}>Bearbeiten</Btn>
                      ) : null}
                      {canLoadSetup ? (
                        <>
                          <Btn color="ghost" size="sm" onClick={() => loadSetupIntoForm(setup)}>Laden</Btn>
                        </>
                      ) : null}
                      <Btn color="ghost" size="sm" onClick={() => runSetupNow(setup.id)} disabled={runningSetupId === setup.id}>
                        {runningSetupId === setup.id ? 'Starte ...' : 'Jetzt ausführen'}
                      </Btn>
                      {canDeleteSetup ? (
                        <Btn color="ghost" size="sm" onClick={() => deleteSetup(setup.id)} disabled={deletingSetupId === setup.id}>
                          {deletingSetupId === setup.id ? 'Lösche ...' : 'Löschen'}
                        </Btn>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderRequestStatusBadge = (status, executionStatus = '', brokerStatus = '') => {
    const normalized = String(status || '').toLowerCase();
    const displayState = getExecutionDisplayState(executionStatus || status, brokerStatus);
    if (normalized === 'done') return <Badge color="green">Fertig</Badge>;
    if (normalized === 'failed') return <Badge color="red">Fehlgeschlagen</Badge>;
    if (normalized === 'approved') return <Badge color="indigo">Freigegeben</Badge>;
    if (normalized === 'requested') return <Badge color="ghost">Angefordert</Badge>;
    if (normalized === 'skipped') return <Badge color="ghost">Übersprungen</Badge>;
    if (normalized === 'discarded') return <Badge color="ghost">Verworfen</Badge>;
    if (displayState === 'executing') return <Badge color="yellow">Wird gesendet</Badge>;
    if (displayState === 'pending_submit') return <Badge color="yellow">PendingSubmit</Badge>;
    if (displayState === 'pre_submitted') return <Badge color="indigo">PreSubmitted</Badge>;
    if (displayState === 'submitted') return <Badge color="green">Submitted</Badge>;
    if (normalized === 'partially_filled') return <Badge color="yellow">Teilweise gefüllt</Badge>;
    if (normalized === 'filled') return <Badge color="green">Gefüllt</Badge>;
    if (normalized === 'canceled') return <Badge color="ghost">{executionStatus ? 'Storniert' : 'Abgebrochen'}</Badge>;
    if (normalized === 'execution_failed') return <Badge color="red">Trade fehlgeschlagen</Badge>;
    if (normalized === 'claimed') return <Badge color="yellow">In Arbeit</Badge>;
    if (normalized === 'expired') return <Badge color="ghost">Abgelaufen</Badge>;
    return <Badge color="ghost">Wartet</Badge>;
  };

  const cancelPreviewRequest = async (requestId) => {
    if (!requestId) return;
    if (!window.confirm('Diesen Preview-Request wirklich abbrechen?')) return;
    setCancelingRequestId(requestId);
    setRequestError('');
    setRequestMessage('');
    try {
      await apiRequest('/trade-journal/live-agent/order-preview/cancel', {
        method: 'POST',
        body: { request_id: requestId },
      });
      await loadPreviewRequests({ silent: true });
      onToast?.('Preview-Request abgebrochen.', 'success');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired?.();
        return;
      }
      const message = err instanceof ApiError ? err.message : 'Preview-Request konnte nicht abgebrochen werden.';
      setRequestError(message);
      onToast?.(message, 'error');
    } finally {
      setCancelingRequestId('');
    }
  };

  const approvePreviewRequest = async (requestId) => {
    if (!requestId || approvingRequestId) return;
    if (!agentIsCurrentlyConnected) {
      const message = 'Agent nicht verbunden.';
      setRequestError(message);
      onToast?.(message, 'error');
      return;
    }
    if (!window.confirm('Diesen gefundenen Trade zur Ausführung an TWS freigeben?')) return;
    setApprovingRequestId(requestId);
    setRequestError('');
    setRequestMessage('');
    try {
      await apiRequest('/trade-journal/live-agent/order-preview/approve', {
        method: 'POST',
        body: { request_id: requestId },
      });
      await loadPreviewRequests({ silent: true });
      onToast?.('Trade zur Ausführung freigegeben.', 'success');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired?.();
        return;
      }
      const message = err instanceof ApiError ? err.message : 'Trade konnte nicht freigegeben werden.';
      setRequestError(message);
      onToast?.(message, 'error');
    } finally {
      setApprovingRequestId('');
    }
  };

  const clearPreviewHistory = async () => {
    if (!previewRequests.length && !automationSetupRuns.length) return;
    if (!window.confirm('Den kompletten Preview-Verlauf wirklich löschen?')) return;
    setClearingHistory(true);
    setRequestError('');
    setRequestMessage('');
    try {
      await apiRequest('/trade-journal/live-agent/order-preview/clear', {
        method: 'POST',
      });
      setPreviewRequests([]);
      setAutomationSetupRuns([]);
      onToast?.('Verlauf gelöscht.', 'success');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired?.();
        return;
      }
      const message = err instanceof ApiError ? err.message : 'Preview-Verlauf konnte nicht gelöscht werden.';
      setRequestError(message);
      onToast?.(message, 'error');
    } finally {
      setClearingHistory(false);
    }
  };

  const clearAgentError = async () => {
    if (clearingAgentError) return;
    setClearingAgentError(true);
    try {
      const data = await apiRequest('/trade-journal/live-agent/clear-error', {
        method: 'POST',
      });
      onLiveAgentErrorCleared?.(data);
      onToast?.('Letzter Fehler bestätigt.', 'success');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired?.();
        return;
      }
      const message = err instanceof ApiError ? err.message : 'Letzter Fehler konnte nicht bestätigt werden.';
      onToast?.(message, 'error');
    } finally {
      setClearingAgentError(false);
    }
  };

  const tradeRows = useMemo(() => {
    const rows = (automationTrades || [])
      .map((item) => {
        const contract = item?.contract || {};
        const contractLegs = getAutomationContractLegs({ contract, symbol: item.symbol, quantity: item.current_quantity ?? item.quantity, currentQuantity: item.current_quantity ?? item.quantity, currency: contract.currency || 'USD' });
        const isStockTrade = isStockAutomationTrade(item);
        const lifecycleStatus = String(item.status || '').toLowerCase();
        const executionStatus = String(item.entry_status || item.status || '').toLowerCase();
        const closeStatus = String(item.close_status || '').toLowerCase();
        const openAt = item.created_at || '';
        return {
          tradeId: item.id,
          lifecycleStatus,
          status: executionStatus,
          symbol: item.symbol || contract.symbol || '—',
          strike: item.strike ?? contract.strike ?? null,
          twsTargetLabel: item.tws_target_label || '—',
          optionsProfile: item.profile_label || buildOptionsProfile(item, contractLegs.length ? contractLegs : [contract]),
          contractLabel: item.local_symbol || contract.local_symbol || `${contract.symbol || ''} ${contract.right || ''} ${contract.expiry || ''} ${contract.strike || ''}`.trim(),
          quantity: item.current_quantity ?? item.quantity ?? '—',
          currentQuantity: item.current_quantity ?? item.quantity ?? null,
          entryAction: item.entry_action || '',
          entryPrice: item.entry_price ?? null,
          brokerStatus: item.entry_broker_status || '—',
          orderId: item.entry_order_id || '—',
          openAt,
          livePnl: item.live_pnl ?? null,
          markPrice: item.mark_price ?? null,
          right: item.right || contract.right || '',
          expiry: item.expiry || contract.expiry || '',
          multiplier: inferAutomationMultiplier({
            ...item,
            contract,
            symbol: item.symbol || contract.symbol,
            localSymbol: item.local_symbol || contract.local_symbol,
          }),
          currency: contract.currency || 'USD',
          currentUnderlyingPrice: resolveAutomationCurrentUnderlyingPrice({ ...item, contract }),
          underlyingPriceCurrency: resolveAutomationUnderlyingPriceCurrency({ ...item, contract }),
          contract,
          contractLegs,
          isStockTrade,
          livePnlPercent: (
            Number.isFinite(Number(item.live_pnl))
            && Number.isFinite(Number(item.entry_price))
            && Number(item.entry_price) > 0
            && Number.isFinite(Number(item.current_quantity ?? item.quantity))
            && Number(item.current_quantity ?? item.quantity) !== 0
            && Number.isFinite(Number(getDetailMultiplier({
              multiplier: item.multiplier,
              contract,
            })))
          )
            ? (
              Number(item.live_pnl)
              / Math.abs(
                Number(item.entry_price)
                * Number(item.current_quantity ?? item.quantity)
                * Number(getDetailMultiplier({
                  multiplier: item.multiplier,
                  contract,
                }))
              )
            ) * 100
            : null,
          strategyLabel: formatStrategyTitle(item.strategy_key),
          canClose: ['open', 'partially_filled', 'filled'].includes(lifecycleStatus),
          canCancel: ['approved', 'executing', 'submitted'].includes(lifecycleStatus) && !['requested', 'processing'].includes(String(item.entry_cancel_status || '').toLowerCase()),
          canResetClose: Boolean(closeStatus),
          canDelete: true,
          closeStatus: item.close_status || '',
          entryCancelStatus: item.entry_cancel_status || '',
          takeProfitPercent: item.take_profit_percent ?? null,
          stopLossPercent: item.stop_loss_percent ?? null,
          exitAttempts: item.exit_attempts ?? null,
          exitWaitSeconds: item.exit_wait_seconds ?? null,
          exitAdjustment: item.exit_adjustment ?? null,
          exitMarketOnLastAttempt: Boolean(item.exit_market_on_last_attempt),
          closeReason: item.close_reason || '',
          closeOrderKind: item.close_order_kind || '',
        };
      })
      .filter(Boolean);
    return rows.sort((a, b) => {
      switch (tradeSort.key) {
        case 'status':
          return compareValues(renderTradeStatusLabel(a.status, a.brokerStatus), renderTradeStatusLabel(b.status, b.brokerStatus), tradeSort.direction);
        case 'openAt': {
          const aTime = parseApiDate(a.openAt)?.getTime() || 0;
          const bTime = parseApiDate(b.openAt)?.getTime() || 0;
          return compareValues(aTime, bTime, tradeSort.direction);
        }
        case 'symbol':
          return compareValues(a.symbol, b.symbol, tradeSort.direction);
        case 'strike':
          return compareValues(a.strike, b.strike, tradeSort.direction);
        case 'entryPrice':
          return compareValues(a.entryPrice, b.entryPrice, tradeSort.direction);
        case 'livePnl':
          return compareValues(a.livePnl, b.livePnl, tradeSort.direction);
        case 'twsTargetLabel':
          return compareValues(a.twsTargetLabel, b.twsTargetLabel, tradeSort.direction);
        case 'optionsProfile':
          return compareValues(a.optionsProfile, b.optionsProfile, tradeSort.direction);
        case 'contractLabel':
          return compareValues(a.contractLabel, b.contractLabel, tradeSort.direction);
        case 'orderId':
          return compareValues(a.orderId, b.orderId, tradeSort.direction);
        default: {
          const aTime = parseApiDate(a.openAt)?.getTime() || 0;
          const bTime = parseApiDate(b.openAt)?.getTime() || 0;
          return compareValues(aTime, bTime, 'desc');
        }
      }
    });
  }, [automationTrades, tradeSort]);

  const toggleTradeSort = (key) => {
    setTradeSort((current) => (
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'openAt' ? 'desc' : 'asc' }
    ));
  };

  const toggleExpandedTrade = (tradeId) => {
    setExpandedTradeIds((current) => ({
      ...current,
      [Number(tradeId)]: !current[Number(tradeId)],
    }));
  };

  const renderTradeSortHeader = (label, key, align = 'left') => {
    const active = tradeSort.key === key;
    const arrow = active ? (tradeSort.direction === 'asc' ? '↑' : '↓') : '↕';
    return (
      <button
        type="button"
        onClick={() => toggleTradeSort(key)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: 0,
          border: 'none',
          background: 'transparent',
          color: active ? COLORS.cyan : COLORS.muted,
          fontSize: '12px',
          cursor: 'pointer',
          fontWeight: active ? 700 : 500,
          textAlign: align,
        }}
      >
        <span>{label}</span>
        <span style={{ fontSize: '11px' }}>{arrow}</span>
      </button>
    );
  };

  const closeAutomationTrade = async (tradeId, orderKind = 'limit') => {
    if (!tradeId || closingTradeId || deletingTradeId) return;
    const normalizedOrderKind = String(orderKind || '').trim().toLowerCase() === 'market' ? 'market' : 'limit';
    setClosingTradeId(Number(tradeId));
    try {
      await apiRequest('/trade-journal/live-agent/automation-trades/close', {
        method: 'POST',
        body: { trade_id: Number(tradeId), order_kind: normalizedOrderKind },
      });
      await loadAutomationTrades({ silent: true });
      setCloseTradeDialog(null);
      onToast?.(`Close-Request als ${normalizedOrderKind === 'market' ? 'Market' : 'Limit'} an den Agenten übergeben.`, 'success');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired?.();
        return;
      }
      onToast?.(err instanceof ApiError ? err.message : 'Trade konnte nicht zum Schließen markiert werden.', 'error');
    } finally {
      setClosingTradeId(0);
    }
  };

  const startEditStockPosition = (row) => {
    if (!row?.isStockTrade) return;
    const contract = row.contract || {};
    const twsTarget = String(row.twsTargetLabel || row.tws_target || '').trim().toLowerCase() === 'live' ? 'live' : 'paper';
    const nextDraft = {
      parent_trade_id: String(row.tradeId || ''),
      symbol: row.symbol || contract.symbol || '',
      exchange: contract.exchange || 'SMART',
      currency: contract.currency || row.currency || 'USD',
      primary_exchange: contract.primary_exchange || contract.primaryExchange || '',
      action: 'BUY',
      quantity: '1',
      order_type: 'MKT',
      limit_price: row.markPrice != null ? String(row.markPrice) : '',
      tws_target: twsTarget,
      take_profit_percent: row.takeProfitPercent != null ? String(row.takeProfitPercent) : '',
      stop_loss_percent: row.stopLossPercent != null ? String(row.stopLossPercent) : '',
    };
    const contractForSelection = {
      ...contract,
      symbol: row.symbol || contract.symbol || '',
      exchange: contract.exchange || 'SMART',
      currency: contract.currency || row.currency || 'USD',
      primary_exchange: contract.primary_exchange || contract.primaryExchange || '',
      local_symbol: contract.local_symbol || row.contractLabel || '',
      display: row.contractLabel || `${row.symbol || contract.symbol || ''} STK ${contract.exchange || 'SMART'} ${contract.currency || row.currency || 'USD'}`.trim(),
    };
    setOrderProductType('stock');
    setOrderSectionCollapsed(false);
    setRequestError('');
    setRequestMessage('');
    setStockResolverStatus(null);
    setSelectedStockContract(contractForSelection);
    setStockDraft(nextDraft);
    submitStockResolverRequest({ silent: true, keepSelection: true, draftOverride: nextDraft });
  };

  const cancelEditStockPosition = () => {
    setStockDraft({
      parent_trade_id: '',
      symbol: 'AAPL',
      exchange: 'SMART',
      currency: 'USD',
      primary_exchange: '',
      action: 'BUY',
      quantity: '1',
      order_type: 'MKT',
      limit_price: '',
      tws_target: 'paper',
      take_profit_percent: '',
      stop_loss_percent: '',
    });
    setSelectedStockContract(null);
    setStockResolverStatus(null);
    setRequestError('');
    setRequestMessage('');
  };

  const cancelAutomationTrade = async (tradeId) => {
    if (!tradeId || closingTradeId || deletingTradeId) return;
    if (!window.confirm('Diesen noch nicht eröffneten Trade verwerfen?')) return;
    setClosingTradeId(Number(tradeId));
    try {
      await apiRequest('/trade-journal/live-agent/automation-trades/cancel', {
        method: 'POST',
        body: { trade_id: Number(tradeId) },
      });
      await Promise.all([loadAutomationTrades({ silent: true }), loadPreviewRequests()]);
      onToast?.('Trade verworfen.', 'success');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired?.();
        return;
      }
      onToast?.(err instanceof ApiError ? err.message : 'Trade konnte nicht verworfen werden.', 'error');
    } finally {
      setClosingTradeId(0);
    }
  };

  const resetAutomationTradeCloseStatus = async (tradeId) => {
    if (!tradeId || closingTradeId || deletingTradeId) return;
    if (!window.confirm('Close-Status freigeben? Bitte vorher prüfen, ob in TWS noch eine offene Close-Order liegt.')) return;
    setClosingTradeId(Number(tradeId));
    try {
      await apiRequest('/trade-journal/live-agent/automation-trades/reset-close', {
        method: 'POST',
        body: { trade_id: Number(tradeId) },
      });
      await loadAutomationTrades({ silent: true });
      onToast?.('Close-Status freigegeben. Der Trade kann wieder geschlossen werden.', 'success');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired?.();
        return;
      }
      onToast?.(err instanceof ApiError ? err.message : 'Close-Status konnte nicht freigegeben werden.', 'error');
    } finally {
      setClosingTradeId(0);
    }
  };

  const deleteAutomationTrade = async (tradeId) => {
    if (!tradeId || closingTradeId || deletingTradeId) return;
    if (!window.confirm('Diesen Eintrag nur aus der Automation entfernen? Offene Positionen in TWS werden dadurch nicht beendet.')) return;
    setDeletingTradeId(Number(tradeId));
    try {
      await apiRequest('/trade-journal/live-agent/automation-trades/delete', {
        method: 'POST',
        body: { trade_id: Number(tradeId) },
      });
      await Promise.all([loadAutomationTrades({ silent: true }), loadPreviewRequests()]);
      onToast?.('Trade aus Automation entfernt.', 'success');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired?.();
        return;
      }
      onToast?.(err instanceof ApiError ? err.message : 'Trade konnte nicht aus der Automation entfernt werden.', 'error');
    } finally {
      setDeletingTradeId(0);
    }
  };

  const startEditExit = (row) => {
    if (!row?.tradeId) return;
    setExpandedTradeIds((current) => ({ ...current, [Number(row.tradeId)]: true }));
    setEditingTradeId(Number(row.tradeId));
    setExitDrafts((current) => ({
      ...current,
      [Number(row.tradeId)]: {
        takeProfitPercent: row.takeProfitPercent != null ? String(row.takeProfitPercent) : '',
        stopLossPercent: row.stopLossPercent != null ? String(row.stopLossPercent) : '',
      },
    }));
  };

  const cancelEditExit = (tradeId) => {
    setEditingTradeId(0);
    setExitDrafts((current) => {
      const next = { ...current };
      delete next[Number(tradeId)];
      return next;
    });
  };

  const updateExitDraft = (tradeId, key, value) => {
    setExitDrafts((current) => ({
      ...current,
      [Number(tradeId)]: {
        ...(current[Number(tradeId)] || {}),
        [key]: value,
      },
    }));
  };

  const saveExitSettings = async (tradeId) => {
    const draft = exitDrafts[Number(tradeId)] || {};
    const row = tradeRows.find((item) => item.tradeId === Number(tradeId));
    const parseNullableNumber = (value) => {
      const text = String(value ?? '').trim().replace(',', '.');
      if (!text) return null;
      const numeric = Number(text);
      if (!Number.isFinite(numeric) || numeric < 0) {
        throw new Error('Bitte nur positive Zahlen für TP/SL verwenden.');
      }
      return numeric;
    };
    try {
      const stopLossPercent = parseNullableNumber(draft.stopLossPercent);
      const longOptionStopLossError = getLongOptionStopLossError(stopLossPercent, isLongOptionTrade(row));
      if (longOptionStopLossError) {
        throw new Error(longOptionStopLossError);
      }
      setSavingExitTradeId(Number(tradeId));
      await apiRequest('/trade-journal/live-agent/automation-trades/update-exit', {
        method: 'POST',
        body: {
          trade_id: Number(tradeId),
          take_profit_percent: parseNullableNumber(draft.takeProfitPercent),
          stop_loss_percent: stopLossPercent,
        },
      });
      await loadAutomationTrades({ silent: true });
      setEditingTradeId(0);
      setExitDrafts((current) => {
        const next = { ...current };
        delete next[Number(tradeId)];
        return next;
      });
      onToast?.('TP/SL gespeichert.', 'success');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired?.();
        return;
      }
      onToast?.(err instanceof ApiError ? err.message : err.message || 'TP/SL konnten nicht gespeichert werden.', 'error');
    } finally {
      setSavingExitTradeId(0);
    }
  };

  const renderExitConfiguration = (row) => {
    if (!row?.tradeId) return null;
    const isEditing = editingTradeId === row.tradeId;
    return (
      <div style={{ display: 'grid', gap: '8px', maxWidth: '420px' }}>
        <div style={{ color: COLORS.muted, fontSize: '11px' }}>Exit-Konfiguration</div>
        {isEditing ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: COLORS.muted, fontSize: '11px', marginBottom: '4px' }}>TP %</div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={exitDrafts[row.tradeId]?.takeProfitPercent ?? ''}
                  onChange={(event) => updateExitDraft(row.tradeId, 'takeProfitPercent', event.target.value)}
                  style={{
                    ...AUTOMATION_FIELD_STYLE,
                    width: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                    borderRadius: '10px',
                    border: `1px solid ${COLORS.border}`,
                    padding: '8px 10px',
                  }}
                />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: COLORS.muted, fontSize: '11px', marginBottom: '4px' }}>SL %</div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={exitDrafts[row.tradeId]?.stopLossPercent ?? ''}
                  onChange={(event) => updateExitDraft(row.tradeId, 'stopLossPercent', event.target.value)}
                  style={{
                    ...AUTOMATION_FIELD_STYLE,
                    width: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                    borderRadius: '10px',
                    border: `1px solid ${COLORS.border}`,
                    padding: '8px 10px',
                  }}
                />
                {isLongOptionTrade(row) && getLongOptionStopLossError(exitDrafts[row.tradeId]?.stopLossPercent, true) ? (
                  <div style={{ color: COLORS.red, fontSize: '11px', lineHeight: 1.5, marginTop: '6px' }}>
                    {getLongOptionStopLossError(exitDrafts[row.tradeId]?.stopLossPercent, true)}
                  </div>
                ) : null}
              </div>
            </div>
            <div style={{ color: COLORS.muted, fontSize: '11px', lineHeight: 1.6 }}>
              {`${row.exitAttempts ?? '—'}x · ${row.exitWaitSeconds ?? '—'}s · ${row.exitAdjustment != null ? formatGermanPrice(row.exitAdjustment) : '—'}`}
              {row.exitMarketOnLastAttempt ? ' · letzter Versuch MKT' : ''}
              {row.closeReason ? ` · ${row.closeReason}` : ''}
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Btn
                color="ghost"
                size="sm"
                onClick={() => saveExitSettings(row.tradeId)}
                disabled={savingExitTradeId === row.tradeId}
              >
                {savingExitTradeId === row.tradeId ? 'Speichere ...' : 'Speichern'}
              </Btn>
              <Btn
                color="ghost"
                size="sm"
                onClick={() => cancelEditExit(row.tradeId)}
                disabled={savingExitTradeId === row.tradeId}
              >
                Abbrechen
              </Btn>
            </div>
          </>
        ) : (
          <div style={{ color: COLORS.text, fontSize: '13px' }}>
            <div>
              {row.takeProfitPercent != null ? `TP ${formatGermanPrice(row.takeProfitPercent)}%` : 'TP aus'}
              {' · '}
              {row.stopLossPercent != null ? `SL ${formatGermanPrice(row.stopLossPercent)}%` : 'SL aus'}
            </div>
            <div style={{ color: COLORS.muted, fontSize: '11px', marginTop: '6px' }}>
              {`${row.exitAttempts ?? '—'}x · ${row.exitWaitSeconds ?? '—'}s · ${row.exitAdjustment != null ? formatGermanPrice(row.exitAdjustment) : '—'}`}
              {row.exitMarketOnLastAttempt ? ' · letzter Versuch MKT' : ''}
              {row.closeReason ? ` · ${row.closeReason}` : ''}
            </div>
          </div>
        )}
      </div>
    );
  };

  const previewRows = useMemo(
    () => {
      const setupsById = new Map(automationSetups.map((setup) => [Number(setup.id), setup]));
      const runsById = new Map(automationSetupRuns.map((run) => [Number(run.id), run]));
      return previewRequests.slice(0, 10).map((item) => {
      const setup = setupsById.get(Number(item.setup_id));
      const setupRun = runsById.get(Number(item.setup_run_id));
      const setupFromRun = setupRun ? setupsById.get(Number(setupRun.setup_id)) : null;
      const resolvedSetupName =
        String(setup?.name || '').trim()
        || String(setupRun?.setup_name || '').trim()
        || String(setupFromRun?.name || '').trim();
      const result = item.result || {};
      const execution = result.execution || {};
      const legs = Array.isArray(result.legs) ? result.legs : [];
      const warnings = Array.isArray(result.warnings) ? result.warnings.filter(Boolean).map((entry) => String(entry).trim()).filter(Boolean) : [];
      const firstLeg = legs[0] || {};
      const symbol = result?.underlying?.symbol || firstLeg.symbol || '—';
      const expiry = result.expiry ? formatGermanDateCompact(result.expiry) : '—';
      const executionStatus = String(execution.status || '').toLowerCase();
      const isOpenRequest = ['pending', 'claimed'].includes(String(item.status || '').toLowerCase());
      const canApproveTrade =
        String(item.status || '').toLowerCase() === 'done' &&
        legs.length > 0 &&
        !['approved', 'executing', 'submitted', 'partially_filled', 'filled'].includes(executionStatus);
      const detailLines = buildPreviewDetailLines({
        strategyKey: item.strategy_key,
        symbol,
        expiry: result.expiry,
        legs,
      });
      return {
        id: item.request_id,
        strategyLabel: resolvedSetupName || formatStrategyTitle(item.strategy_key),
        symbol,
        expiry,
        status: item.status,
        executionStatus,
        brokerStatus: execution.broker_status,
        requestedAt: item.requested_at,
        legCount: legs.length,
        warningCount: warnings.length,
        errorMessage: String(item.error_message || '').trim(),
        detailLines,
        detailText: detailLines.join(' · '),
        infoLines: buildPreviewInfoLines({
          result,
          legs,
          warnings,
          errorMessage: item.error_message,
        }),
        canApproveTrade,
        isOpenRequest,
      };
    });
    },
    [automationSetups, automationSetupRuns, previewRequests],
  );

  const filterSetupRow = useCallback((setup) => {
    const symbol = String(setup?.field_values?.underlying_symbol || '').trim().toUpperCase();
    const weekday = Number(setup?.schedule?.weekday);
    if (setupSymbolFilter !== 'ALLE' && symbol !== setupSymbolFilter) return false;
    if (setupWeekdayFilter !== 'ALLE' && weekday !== Number(setupWeekdayFilter)) return false;
    return true;
  }, [setupSymbolFilter, setupWeekdayFilter]);

  const filteredSetupRows = useMemo(() => {
    return automationSetups.filter((setup) => {
      if (setup?.is_template) return false;
      return filterSetupRow(setup);
    });
  }, [automationSetups, filterSetupRow]);

  const filteredTemplateRows = useMemo(() => {
    return templateSetups.filter((setup) => filterSetupRow(setup));
  }, [templateSetups, filterSetupRow]);

  const allSetupRows = useMemo(() => ([
    ...automationSetups,
    ...templateSetups,
  ]), [automationSetups, templateSetups]);

  const filteredSetupRunRows = useMemo(() => {
    const setupsById = new Map(allSetupRows.map((setup) => [Number(setup.id), setup]));
    return automationSetupRuns.filter((run) => {
      const setup = setupsById.get(Number(run.setup_id));
      if (!setup) return true;
      return filterSetupRow(setup);
    }).map((run) => {
      const setup = setupsById.get(Number(run.setup_id));
      return {
        ...run,
        symbol: String(setup?.field_values?.underlying_symbol || '').trim().toUpperCase() || '—',
        weekdayLabel: getAutomationWeekdayLabel(setup?.schedule?.weekday),
        strategyLabel: formatStrategyTitle(setup?.strategy_key || ''),
      };
    });
  }, [allSetupRows, automationSetupRuns, filterSetupRow]);

  const combinedHistoryRows = useMemo(() => {
    const previewEntries = previewRows.map((row) => ({
      id: `preview-${row.id}`,
      kind: 'Preview',
      createdAt: row.requestedAt,
      strategyLabel: row.strategyLabel,
      symbol: row.symbol,
      detailLines: row.detailLines,
      detail: row.detailText,
      status: { status: row.status, executionStatus: row.executionStatus, brokerStatus: row.brokerStatus },
      infoLines: row.infoLines,
      canApproveTrade: row.canApproveTrade,
      isOpenRequest: row.isOpenRequest,
      requestId: row.id,
    }));
    const runEntries = filteredSetupRunRows.map((run) => ({
      id: `run-${run.id}`,
      kind: 'Run',
      createdAt: run.created_at || run.started_at || run.scheduled_for,
      strategyLabel: run.setup_name || `Setup ${run.setup_id}`,
      symbol: run.symbol,
      detail: `${run.weekdayLabel} · ${run.strategyLabel}`,
      status: { status: run.status, executionStatus: '', brokerStatus: '' },
      infoLines: [run.skip_reason || (run.preview_request_id ? `Preview ${String(run.preview_request_id).slice(0, 8)}` : '—')],
      canApproveTrade: false,
      isOpenRequest: false,
      requestId: '',
    }));
    return [...previewEntries, ...runEntries].sort((left, right) => {
      const leftTime = parseApiDate(left.createdAt)?.getTime() || 0;
      const rightTime = parseApiDate(right.createdAt)?.getTime() || 0;
      return rightTime - leftTime;
    });
  }, [filteredSetupRunRows, previewRows]);

  const stockResolverResult = stockResolverStatus?.result || {};
  const stockResolverMatches = Array.isArray(stockResolverResult.matches) ? stockResolverResult.matches : [];
  const stockResolverState = String(stockResolverStatus?.status || '').trim().toLowerCase();
  const stockResolverIsPending = ['pending', 'processing'].includes(stockResolverState);
  return (
    <div>
      <Card style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px' }}>
          <div style={{ fontSize: '16px', fontWeight: '800', color: COLORS.text }}>Einfach-Trading.de Live Agent</div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <AgentDownloadControl COLORS={COLORS} downloadInfo={agentDownloadInfo} />
            <span style={{ color: COLORS.muted, fontSize: '12px' }}>
              {tradeJournalAgentName ? `Name: ${tradeJournalAgentName}` : 'Noch kein Agent verbunden'}
            </span>
            <span style={{ color: COLORS.border, fontSize: '13px' }}>•</span>
            <span style={{ color: COLORS.muted, fontSize: '12px' }}>
              {tradeJournalAgentSecretSet ? 'Secret gesetzt' : 'Secret fehlt'}
            </span>
            <span style={{ color: COLORS.border, fontSize: '13px' }}>•</span>
            <Badge
              color={
                tradeJournalAgentStatusColor === 'green'
                  ? 'green'
                  : tradeJournalAgentStatusColor === 'red'
                    ? 'red'
                    : tradeJournalAgentStatusColor === 'yellow'
                      ? 'yellow'
                      : 'ghost'
              }
            >
              {tradeJournalAgentStatusLabel || 'Nicht eingerichtet'}
            </Badge>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: tradeJournalAgentLastError ? '10px' : 0 }}>
          {tradeJournalAgentLastSeenAt ? (
            <Badge color={tradeJournalAgentStatusColor === 'green' ? 'green' : 'ghost'}>
              Zuletzt aktiv {formatRelativeAge(tradeJournalAgentLastSeenAt)}
            </Badge>
          ) : null}
          {renderTargetTwsBadge('Live TWS', tradeJournalAgentLiveTwsConnected, tradeJournalAgentLiveLastTwsConnectedAt)}
          {renderTargetTwsBadge('Paper TWS', tradeJournalAgentPaperTwsConnected, tradeJournalAgentPaperLastTwsConnectedAt)}
          {tradeJournalAgentLastQuoteDurationMs !== null && tradeJournalAgentLastQuoteDurationMs !== undefined ? (
            <Badge color="ghost">Quote {formatDurationMs(tradeJournalAgentLastQuoteDurationMs)}</Badge>
          ) : null}
        </div>
        {tradeJournalAgentLastError ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap',
              padding: '10px 12px',
              borderRadius: '12px',
              border: `1px solid ${COLORS.red}33`,
              background: '#ef444410',
            }}
          >
            <div style={{ color: COLORS.red, fontSize: '12px', lineHeight: 1.7 }}>
              <strong>Letzter Fehler:</strong> {tradeJournalAgentLastError}
              {tradeJournalAgentLastErrorAt ? (
                <span style={{ color: COLORS.muted }}>
                  {' '}
                  · {formatDateTime(tradeJournalAgentLastErrorAt)}
                </span>
              ) : null}
            </div>
            <Btn
              type="button"
              color="ghost"
              size="sm"
              disabled={clearingAgentError}
              onClick={clearAgentError}
            >
              {clearingAgentError ? 'Bestätige ...' : 'OK'}
            </Btn>
          </div>
        ) : null}
      </Card>


      <div style={{ display: 'flex', flexDirection: 'column' }}>
      <Card style={{ marginBottom: '24px', order: 2 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <div>
              <div style={{ fontSize: '18px', fontWeight: '800', marginBottom: '6px' }}>Order</div>
            </div>
            <Btn color="ghost" size="sm" onClick={() => setOrderSectionCollapsed((current) => !current)}>
              {orderSectionCollapsed ? 'Aufklappen' : 'Zuklappen'}
            </Btn>
          </div>

          {orderSectionCollapsed ? null : loading && (
            <div style={{ color: COLORS.muted, fontSize: '14px' }}>Automation-Capabilities werden geladen ...</div>
          )}

          {orderSectionCollapsed ? null : !loading && error && (
            <div style={{ color: COLORS.red, fontSize: '14px', marginBottom: '10px' }}>⚠ {error}</div>
          )}

          {orderSectionCollapsed ? null : !loading && !error && capabilities && (
            <>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                {[
                  { value: 'option', label: 'Option' },
                  { value: 'stock', label: 'Aktie' },
                ].map((item) => {
                  const active = orderProductType === item.value;
                  return (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => {
                        setOrderProductType(item.value);
                        setRequestMessage('');
                        setRequestError('');
                      }}
                      style={{
                        border: `1px solid ${active ? `${COLORS.cyan}80` : COLORS.border}`,
                        background: active ? COLORS.cyan : '#ffffff08',
                        color: active ? '#052f35' : COLORS.text,
                        borderRadius: '999px',
                        padding: '9px 14px',
                        fontWeight: 800,
                        cursor: 'pointer',
                      }}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>

              {orderProductType === 'option' ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                    {(capabilities.strategies || []).map((strategy) => {
                      const active = strategy.key === selectedStrategyId;
                      return (
                        <button
                          key={strategy.key}
                          type="button"
                          disabled={!canUseOrderForm}
                          onClick={() => {
                            setSelectedStrategyId(strategy.key);
                            setDraftValues(Object.fromEntries((strategy.fields || []).map((field) => [field.key, fieldInitialValue(field)])));
                            setRequestMessage('');
                            setRequestError('');
                          }}
                          style={{
                            textAlign: 'left',
                            borderRadius: '12px',
                            border: `1px solid ${active ? `${COLORS.cyan}55` : COLORS.border}`,
                            background: active ? '#0ea5a614' : '#ffffff05',
                            padding: '12px',
                            cursor: 'pointer',
                            color: COLORS.text,
                          }}
                          title={strategy.description || strategy.label || formatStrategyTitle(strategy.key)}
                        >
                          <div style={{ fontSize: '14px', fontWeight: '800', marginBottom: '6px' }}>{strategy.label || formatStrategyTitle(strategy.key)}</div>
                        </button>
                      );
                    })}
                  </div>

                  {selectedStrategy ? (
                    <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 12px' }}>
                    {visibleFields.map((field) => (
                      <div key={field.key}>
                        <label
                          style={{ display: 'block', fontSize: '12px', color: COLORS.muted, marginBottom: '6px', cursor: field.description ? 'help' : 'default' }}
                          title={field.description || undefined}
                        >
                          {FIELD_LABELS[field.key] || field.label || field.key}
                          {field.description ? ' ⓘ' : ''}
                        </label>
                        {renderField(field)}
                      </div>
                    ))}
                  </div>

                  {requestError ? (
                    <div style={{ marginTop: '16px', color: COLORS.red, fontSize: '13px' }}>⚠ {requestError}</div>
                  ) : null}
                  {requestMessage ? (
                    <div style={{ marginTop: '16px', color: COLORS.green, fontSize: '13px' }}>{requestMessage}</div>
                  ) : null}

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '16px' }}>
                    {!canUseOrderForm ? (
                      <div style={{ color: COLORS.muted, fontSize: '12px' }}>
                        Order-Daten sind hier nur zur Ansicht sichtbar und kommen aus der geladenen Vorlage.
                      </div>
                    ) : null}
                    <Btn color="green" onClick={submitPreviewRequest} disabled={requestLoading || !canUseOrderForm}>
                      {requestLoading ? 'Sende an Agent ...' : previewButtonLabel}
                    </Btn>
                  </div>
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  {stockIsEditingPosition ? (
                    <div style={{ marginTop: '12px', padding: '10px 12px', border: `1px solid ${COLORS.cyan}55`, borderRadius: '12px', background: '#0ea5a614', color: COLORS.text, fontSize: '13px', fontWeight: 700 }}>
	                      {selectedStockContract ? (
	                        <div>
	                          Zugeordneter IBKR-Contract für Position #{stockEditingTradeId}:{' '}
	                          <span style={{ color: COLORS.text }}>
	                            {selectedStockContract.display || `${selectedStockContract.symbol || stockDraft.symbol} STK ${selectedStockContract.exchange || stockDraft.exchange} ${selectedStockContract.currency || stockDraft.currency}`}
	                          </span>
	                        </div>
	                      ) : (
	                        <div>Zugeordneter IBKR-Contract für Position #{stockEditingTradeId}</div>
	                      )}
                    </div>
                  ) : null}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(180px, 1fr))', gap: '12px', marginTop: '14px' }}>
                    {renderStockField('symbol', 'Ticker')}
                    {renderStockField('tws_target', 'TWS-Ziel', 'select', [
                      { value: 'paper', label: 'Paper' },
                      { value: 'live', label: 'Live' },
                    ])}
                  </div>
                  <div style={{ marginTop: '12px', maxWidth: '280px' }}>
                    <Btn color="green" onClick={submitStockResolverRequest} disabled={stockResolverLoading || stockIsEditingPosition || !canUseOrderForm || !agentIsCurrentlyConnected} style={{ width: '100%' }}>
                      {stockResolverLoading || stockResolverIsPending ? `${stockTwsTargetLabel} Ticker sucht ...` : `${stockTwsTargetLabel} Ticker suchen`}
                    </Btn>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(180px, 1fr))', gap: '12px', marginTop: '12px' }}>
                    {renderStockField('action', 'Richtung', 'select', [
                      { value: 'BUY', label: 'Kaufen' },
                      { value: 'SELL', label: 'Verkaufen' },
                    ])}
                    {renderStockField('quantity', 'Menge', 'number')}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: String(stockDraft.order_type || '').toUpperCase() === 'LMT' ? 'repeat(3, minmax(160px, 1fr))' : 'repeat(2, minmax(180px, 1fr))', gap: '12px', marginTop: '12px' }}>
                    {renderStockField('order_type', 'Ordertyp', 'select', [
                      { value: 'MKT', label: 'Market' },
                      { value: 'LMT', label: 'Limit' },
                    ])}
                    {renderStockField('currency', 'Währung')}
                    {String(stockDraft.order_type || '').toUpperCase() === 'LMT'
                      ? renderStockField('limit_price', 'Limitpreis', 'number')
                      : null}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(180px, 1fr))', gap: '12px', marginTop: '12px' }}>
                    {renderStockField('exchange', 'Börse/Route')}
                    {renderStockField('primary_exchange', 'Primary Exchange')}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(180px, 1fr))', gap: '12px', marginTop: '12px' }}>
                    {renderStockField('take_profit_percent', 'TP % initial', 'number')}
                    {renderStockField('stop_loss_percent', 'SL % initial', 'number')}
                  </div>

                  {stockResolverStatus ? (
                    <div style={{ marginTop: '14px', border: `1px solid ${COLORS.border}`, borderRadius: '14px', padding: '12px', background: '#0d1b22' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: stockResolverMatches.length ? '10px' : 0 }}>
                        <strong style={{ fontSize: '13px' }}>IBKR-Contract</strong>
                        <span style={{ fontSize: '12px', color: stockResolverState === 'failed' ? COLORS.red : COLORS.muted }}>
                          {stockResolverIsPending ? 'Agent sucht ...' : stockResolverState === 'done' ? `${stockResolverMatches.length} Treffer` : stockResolverStatus.error_message || 'Noch kein Treffer'}
                        </span>
                      </div>
                      {stockResolverMatches.length ? (
                        <div style={{ display: 'grid', gap: '8px' }}>
                          {stockResolverMatches.map((item, index) => {
                            const conId = String(item.con_id || index);
                            const isSelected = Number(selectedStockContract?.con_id || 0) === Number(item.con_id || -1)
                              || (!selectedStockContract?.con_id && selectedStockContract?.display === item.display);
                            return (
                              <button
                                key={`${conId}-${item.display || index}`}
                                type="button"
                                onClick={() => {
                                  const midPrice = Number(item.mid);
                                  setSelectedStockContract(item);
                                  setStockDraft((current) => ({
                                    ...current,
                                    symbol: item.symbol || current.symbol,
                                    exchange: item.exchange || current.exchange,
                                    currency: item.currency || current.currency,
                                    primary_exchange: item.primary_exchange || current.primary_exchange || '',
                                    limit_price: Number.isFinite(midPrice) && midPrice > 0 ? String(midPrice) : current.limit_price,
                                  }));
                                }}
                                style={{
                                  width: '100%',
                                  textAlign: 'left',
                                  borderRadius: '12px',
                                  border: `1px solid ${isSelected ? COLORS.green : COLORS.border}`,
                                  background: isSelected ? '#10311e' : '#12232b',
                                  color: COLORS.text,
                                  padding: '10px 12px',
                                  cursor: 'pointer',
                                }}
                              >
                                <div style={{ fontWeight: 800 }}>{item.display || `${item.symbol} STK ${item.exchange} ${item.currency}`}</div>
                                <div style={{ marginTop: '5px', color: COLORS.text, fontSize: '12px', fontWeight: 700 }}>
                                  Geld {formatNullablePrice(item.bid, item.currency)} · Mitte {formatNullablePrice(item.mid, item.currency)} · Brief {formatNullablePrice(item.ask, item.currency)}
                                </div>
                                {item.long_name ? (
                                  <div style={{ marginTop: '3px', color: COLORS.muted, fontSize: '12px' }}>{item.long_name}</div>
                                ) : null}
                              </button>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {requestError ? (
                    <div style={{ marginTop: '16px', color: COLORS.red, fontSize: '13px' }}>⚠ {requestError}</div>
                  ) : null}
                  {requestMessage ? (
                    <div style={{ marginTop: '16px', color: COLORS.green, fontSize: '13px' }}>{requestMessage}</div>
                  ) : null}

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '16px' }}>
                    <Btn color="green" onClick={submitStockPreviewRequest} disabled={requestLoading || !canUseOrderForm}>
                      {requestLoading ? 'Sende an Agent ...' : `${stockTwsTargetLabel} Trade prüfen`}
                    </Btn>
                    {stockIsEditingPosition ? (
                      <Btn color="ghost" onClick={cancelEditStockPosition} disabled={requestLoading}>
                        Abbrechen
                      </Btn>
                    ) : null}
                  </div>
                </>
              )}
            </>
          )}
      </Card>

      <Card style={{ marginBottom: '24px', order: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' }}>
              <div style={{ fontSize: '16px', fontWeight: '800' }}>Verlauf</div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {requestsLoading ? <span style={{ color: COLORS.muted, fontSize: '12px' }}>lädt ...</span> : null}
                <Btn
                  color="ghost"
                  onClick={clearPreviewHistory}
                  disabled={clearingHistory || (!previewRequests.length && !automationSetupRuns.length)}
                >
                  {clearingHistory ? 'Lösche ...' : 'Löschen'}
                </Btn>
                <Btn color="ghost" onClick={() => loadPreviewRequests()} disabled={requestsLoading}>Aktualisieren</Btn>
              </div>
            </div>
            {combinedHistoryRows.length === 0 ? (
              <div style={{ color: COLORS.muted, fontSize: '14px' }}>Noch keine Einträge vorhanden.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1040px' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <th style={{ textAlign: 'left', padding: '10px 12px', color: COLORS.muted, fontSize: '12px' }}>Typ</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', color: COLORS.muted, fontSize: '12px' }}>Zeit</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', color: COLORS.muted, fontSize: '12px' }}>Setup</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', color: COLORS.muted, fontSize: '12px' }}>Symbol</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', color: COLORS.muted, fontSize: '12px' }}>Detail</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', color: COLORS.muted, fontSize: '12px' }}>Status</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', color: COLORS.muted, fontSize: '12px' }}>Info</th>
                      <th style={{ textAlign: 'right', padding: '10px 12px', color: COLORS.muted, fontSize: '12px' }}>Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {combinedHistoryRows.map((row) => (
                      <tr key={row.id} style={{ borderBottom: `1px solid ${COLORS.border}55` }}>
                        <td style={{ padding: '12px', color: COLORS.text, fontSize: '13px' }}>
                          {row.kind}
                        </td>
                        <td style={{ padding: '12px', color: COLORS.text, fontSize: '13px' }}>
                          <div>{formatDateTime(row.createdAt)}</div>
                          <div style={{ color: COLORS.muted, fontSize: '11px', marginTop: '4px' }}>{formatRelativeAge(row.createdAt)}</div>
                        </td>
                        <td style={{ padding: '12px', color: COLORS.text, fontSize: '13px', fontWeight: '700' }}>{row.strategyLabel}</td>
                        <td style={{ padding: '12px', color: COLORS.text, fontSize: '13px' }}>{row.symbol}</td>
                        <td style={{ padding: '12px', color: COLORS.text, fontSize: '13px', minWidth: '300px' }}>
                          {Array.isArray(row.detailLines) ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {row.detailLines.map((line, index) => (
                                <div
                                  key={`${row.id}-detail-${index}`}
                                  style={{
                                    color: index === 0 ? COLORS.text : COLORS.muted,
                                    fontWeight: index === 0 ? 600 : 400,
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {line}
                                </div>
                              ))}
                            </div>
                          ) : row.detail}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {renderRequestStatusBadge(row.status.status, row.status.executionStatus, row.status.brokerStatus)}
                        </td>
                        <td style={{ padding: '12px', color: COLORS.text, fontSize: '12px', lineHeight: 1.5, minWidth: '320px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {(Array.isArray(row.infoLines) ? row.infoLines : ['—']).map((line, index) => (
                              <div
                                key={`${row.id}-info-${index}`}
                                style={{
                                  color: line && line !== '—' ? COLORS.text : COLORS.muted,
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {line || '—'}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
                            {row.canApproveTrade ? (
                              <Btn
                                color="green"
                                size="sm"
                                onClick={() => approvePreviewRequest(row.requestId)}
                                disabled={approvingRequestId === row.requestId}
                              >
                                {approvingRequestId === row.requestId ? 'Gebe frei ...' : 'Trade freigeben'}
                              </Btn>
                            ) : null}
                            {row.isOpenRequest ? (
                              <Btn
                                color="ghost"
                                size="sm"
                                onClick={() => cancelPreviewRequest(row.requestId)}
                                disabled={cancelingRequestId === row.requestId}
                              >
                                {cancelingRequestId === row.requestId ? 'Breche ab ...' : 'Abbrechen'}
                              </Btn>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </Card>

      <Card style={{ marginBottom: '24px', order: 3 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
              <div style={{ fontSize: '16px', fontWeight: '800' }}>Setups</div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', color: COLORS.muted, marginBottom: '6px' }}>Symbol</label>
                  <select value={setupSymbolFilter} onChange={(event) => setSetupSymbolFilter(event.target.value)} style={{ minWidth: '104px', borderRadius: '12px', border: `1px solid ${COLORS.border}`, padding: '9px 10px', fontSize: '13px', ...AUTOMATION_FIELD_STYLE, ...AUTOMATION_SELECT_STYLE }}>
                    {AUTOMATION_SETUP_SYMBOL_FILTERS.map((item) => <option key={item} value={item} style={AUTOMATION_SELECT_OPTION_STYLE}>{item}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', color: COLORS.muted, marginBottom: '6px' }}>Wochentag</label>
                  <select value={setupWeekdayFilter} onChange={(event) => setSetupWeekdayFilter(event.target.value)} style={{ minWidth: '124px', borderRadius: '12px', border: `1px solid ${COLORS.border}`, padding: '9px 10px', fontSize: '13px', ...AUTOMATION_FIELD_STYLE, ...AUTOMATION_SELECT_STYLE }}>
                    {AUTOMATION_SETUP_WEEKDAY_FILTERS.map((item) => <option key={item.value} value={item.value} style={AUTOMATION_SELECT_OPTION_STYLE}>{item.label}</option>)}
                  </select>
                </div>
                <Btn color="ghost" size="sm" onClick={() => setSetupSectionCollapsed((current) => !current)}>
                  {setupSectionCollapsed ? 'Aufklappen' : 'Zuklappen'}
                </Btn>
              </div>
            </div>
            {setupSectionCollapsed ? null : (
            <>
              <div style={{ marginBottom: '20px', paddingBottom: '16px', borderBottom: `1px solid ${COLORS.border}` }}>
                {!canUseOrderForm ? (
                  <div style={{ marginBottom: '12px', color: COLORS.muted, fontSize: '12px', lineHeight: 1.7 }}>
                    Für Benutzer ohne Order-Maske werden Order-Daten aus der geladenen Vorlage übernommen. Änderbar sind nur die Setup-Eigenschaften.
                  </div>
                ) : null}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: COLORS.muted, marginBottom: '6px' }}>{setupIsTemplate ? 'Vorlagen-Name' : 'Setup-Name'}</label>
                    <input value={setupName} disabled={!canUseOrderForm} onChange={(event) => setSetupName(event.target.value)} style={{ width: '100%', borderRadius: '12px', border: `1px solid ${COLORS.border}`, padding: '11px 12px', fontSize: '14px', boxSizing: 'border-box', ...AUTOMATION_FIELD_STYLE }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: COLORS.muted, marginBottom: '6px' }}>Max. aktive Trades</label>
                    <input type="number" min="1" step="1" value={setupMaxActiveTrades} disabled={!canUseOrderForm && !hasRestrictedTemplateContext} onChange={(event) => setSetupMaxActiveTrades(event.target.value)} style={{ width: '100%', borderRadius: '12px', border: `1px solid ${COLORS.border}`, padding: '11px 12px', fontSize: '14px', boxSizing: 'border-box', ...AUTOMATION_FIELD_STYLE }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: COLORS.muted, marginBottom: '6px' }}>Wochentag</label>
                    <select value={setupWeekday} disabled={!canUseOrderForm && !hasRestrictedTemplateContext} onChange={(event) => setSetupWeekday(event.target.value)} style={{ width: '100%', borderRadius: '12px', border: `1px solid ${COLORS.border}`, padding: '11px 12px', fontSize: '14px', boxSizing: 'border-box', ...AUTOMATION_FIELD_STYLE, ...AUTOMATION_SELECT_STYLE }}>
                      {AUTOMATION_SETUP_WEEKDAYS.map((item) => <option key={item.value} value={item.value} style={AUTOMATION_SELECT_OPTION_STYLE}>{item.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: COLORS.muted, marginBottom: '6px' }}>Uhrzeit</label>
                    <input type="time" value={setupTimeLocal} disabled={!canUseOrderForm && !hasRestrictedTemplateContext} onChange={(event) => setSetupTimeLocal(event.target.value)} style={{ width: '100%', borderRadius: '12px', border: `1px solid ${COLORS.border}`, padding: '11px 12px', fontSize: '14px', boxSizing: 'border-box', ...AUTOMATION_FIELD_STYLE }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: COLORS.muted, marginBottom: '6px' }}>Zeitzone</label>
                    <input value={setupTimezone} disabled={!canUseOrderForm && !hasRestrictedTemplateContext} onChange={(event) => setSetupTimezone(event.target.value)} style={{ width: '100%', borderRadius: '12px', border: `1px solid ${COLORS.border}`, padding: '11px 12px', fontSize: '14px', boxSizing: 'border-box', ...AUTOMATION_FIELD_STYLE }} />
                  </div>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', paddingTop: '24px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: COLORS.text, fontSize: '13px' }}>
                      <input type="checkbox" checked={setupEnabled} disabled={!canUseOrderForm && !hasRestrictedTemplateContext} onChange={(event) => setSetupEnabled(event.target.checked)} />
                      Aktiv
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: COLORS.text, fontSize: '13px' }}>
                      <input type="checkbox" checked={setupAutoApprove} disabled={!canUseOrderForm && !hasRestrictedTemplateContext} onChange={(event) => setSetupAutoApprove(event.target.checked)} />
                      Auto-Freigabe
                    </label>
                    {isAdmin ? (
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: COLORS.text, fontSize: '13px' }}>
                        <input type="checkbox" checked={setupIsTemplate} onChange={(event) => setSetupIsTemplate(event.target.checked)} />
                        Vorlage erstellen
                      </label>
                    ) : null}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '14px' }}>
                  <Btn color="ghost" onClick={saveCurrentDraftAsSetup} disabled={setupSaving || (!canUseOrderForm && !hasRestrictedTemplateContext)}>
                    {setupSaving ? (editingSetupId ? 'Aktualisiere ...' : 'Speichere ...') : (
                      hasRestrictedTemplateContext
                        ? (editingSetupId ? 'Vorlagen-Setup aktualisieren' : 'Vorlagen-Setup speichern')
                        : (editingSetupId ? (setupIsTemplate ? 'Vorlage aktualisieren' : 'Setup aktualisieren') : (setupIsTemplate ? 'Als Vorlage speichern' : 'Als Setup speichern'))
                    )}
                  </Btn>
                  {editingSetupId ? (
                    <Btn color="ghost" onClick={resetSetupForm} disabled={setupSaving}>
                      Abbrechen
                    </Btn>
                  ) : null}
                </div>
              </div>

            <div style={{ marginBottom: '18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' }}>
                <div style={{ fontSize: '14px', fontWeight: '800' }}>Eigene Setups</div>
                <div style={{ color: COLORS.muted, fontSize: '12px' }}>{filteredSetupRows.length} Einträge</div>
              </div>
              {renderSetupTable(filteredSetupRows)}
            </div>

            <div style={{ paddingTop: '18px', borderTop: `1px solid ${COLORS.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' }}>
                <div style={{ fontSize: '14px', fontWeight: '800' }}>Vorlagen</div>
                <div style={{ color: COLORS.muted, fontSize: '12px' }}>{filteredTemplateRows.length} Einträge</div>
              </div>
              {renderSetupTable(filteredTemplateRows, { templates: true })}
            </div>
            </>
            )}
      </Card>

      <Card style={{ marginBottom: '24px', order: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: '800' }}>Trades</div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Badge color="ghost">{tradeRows.length} aktiv</Badge>
          </div>
        </div>
        {tradeRows.length === 0 ? (
          <div style={{ color: COLORS.muted, fontSize: '14px' }}>
            Noch keine freigegebenen oder gesendeten Trades vorhanden.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1040px' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <th style={{ width: '40px', padding: '10px 8px' }} />
                  <th style={{ textAlign: 'left', padding: '10px 12px' }}>{renderTradeSortHeader('Status', 'status')}</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px' }}>{renderTradeSortHeader('Open', 'openAt')}</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px' }}>{renderTradeSortHeader('Symbol', 'symbol')}</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px' }}>{renderTradeSortHeader('Strike', 'strike')}</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px' }}>{renderTradeSortHeader('Entry', 'entryPrice')}</th>
                  <th style={{ textAlign: 'right', padding: '10px 12px' }}>{renderTradeSortHeader('Live P/L', 'livePnl', 'right')}</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px' }}>{renderTradeSortHeader('TWS', 'twsTargetLabel')}</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px' }}>{renderTradeSortHeader('Optionsprofil', 'optionsProfile')}</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', color: COLORS.muted, fontSize: '12px' }}>Exit</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px' }}>{renderTradeSortHeader('Order', 'orderId')}</th>
                  <th style={{ textAlign: 'right', padding: '10px 12px', color: COLORS.muted, fontSize: '12px' }}>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {tradeRows.map((row) => {
                  const isExpanded = Boolean(expandedTradeIds[row.tradeId]);
                  const pnlColor = getPnlColor(row.livePnl, COLORS);
                  const valueColor = (value) => getPnlColor(value, COLORS);
                  const optionRisk = row.contractLegs.length > 1
                    ? calculateAutomationSpreadRisk(row, row.currency || 'USD')
                    : calculateAutomationOptionRisk(row, row.currency || 'USD');
                  const commissionTotal = getAutomationCommissionTotal(row);
                  const optionProfileMode = optionProfileModes[row.tradeId] === 'today' ? 'today' : 'expiry';
                  const baseOptionProfile = buildAutomationOptionProfile(
                    row,
                    row.currentUnderlyingPrice,
                    row.livePnl,
                    'expiry',
                  );
                  const optionProfileSupportsToday = baseOptionProfile?.currentUnderlyingPrice != null && row.livePnl != null;
                  const optionProfile = optionProfileSupportsToday
                    ? buildAutomationOptionProfile(
                      row,
                      row.currentUnderlyingPrice,
                      row.livePnl,
                      optionProfileMode,
                    )
                    : baseOptionProfile;
                  const optionBreakEvenDistance = optionProfile ? buildBreakEvenDistance(optionProfile) : null;
                  const scenarioPrice = optionProfile
                    ? Number(optionProfileScenarioPrices[row.tradeId] ?? optionProfile.currentUnderlyingPrice ?? optionProfile.minPrice)
                    : null;
                  const scenarioPnl = optionProfile ? interpolateProfilePnl(optionProfile.points, scenarioPrice) : null;
                  const optionGreeks = optionProfile ? calculateApproxGreeks(optionProfile, row) : null;
                  const signedQuantity = (String(row.entryAction || '').trim().toUpperCase() === 'SELL' ? -1 : 1)
                    * Math.abs(Number(row.currentQuantity ?? row.quantity ?? 0));
                  const holdDays = (() => {
                    const openedAt = parseApiDate(row.openAt);
                    if (!openedAt) return '—';
                    const start = new Date(openedAt);
                    start.setHours(0, 0, 0, 0);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return `${Math.max(0, Math.floor((today.getTime() - start.getTime()) / 86400000))}d`;
                  })();
                  const markValue = (
                    Number.isFinite(Number(row.markPrice))
                    && Number.isFinite(Number(row.currentQuantity ?? row.quantity))
                  )
                    ? (String(row.entryAction || '').trim().toUpperCase() === 'SELL' ? -1 : 1)
                      * Math.abs(Number(row.currentQuantity ?? row.quantity))
                      * Number(row.markPrice)
                      * Number(getDetailMultiplier(row))
                    : null;
                  const detailLegRows = row.contractLegs.length > 0
                    ? row.contractLegs.map((leg, index) => ({
                      id: `${row.tradeId}-leg-${index}`,
                      label: [
                        leg?.expiry ? formatDateValue(leg.expiry) : null,
                        leg?.strike != null ? `${formatGermanStrike(leg.strike)}${leg.right || ''}` : (leg?.right || null),
                      ].filter(Boolean).join(' · ') || `Leg ${index + 1}`,
                      netQty: leg.action === 'BUY' ? Math.abs(Number(leg.quantity || 0)) : -Math.abs(Number(leg.quantity || 0)),
                      multiplier: Number(leg.multiplier || inferAutomationMultiplier(leg)),
                      bid: leg.bid,
                      ask: leg.ask,
                      lastPrice: leg.last_price ?? leg.last,
                      markPrice: leg.current_mark_price,
                      source: leg.mark_source || 'contract',
                      fxRate: leg.fx_rate_to_base,
                      marketValue: (
                        Number.isFinite(Number(leg.current_mark_price))
                        && Number.isFinite(Number(leg.quantity))
                      )
                        ? (leg.action === 'BUY' ? 1 : -1)
                          * Math.abs(Number(leg.quantity))
                          * Number(leg.current_mark_price)
                          * Number(leg.multiplier || inferAutomationMultiplier(leg))
                        : null,
                    }))
                    : [];
                  const detailDate = parseApiDate(row.openAt);
                  return (
                    <React.Fragment key={row.tradeId}>
                      <tr style={{ borderBottom: isExpanded ? 'none' : `1px solid ${COLORS.border}55` }}>
                        <td style={{ padding: '12px 8px' }}>
                          <button
                            type="button"
                            onClick={() => toggleExpandedTrade(row.tradeId)}
                            style={{
                              width: '26px',
                              height: '26px',
                              borderRadius: '999px',
                              border: `1px solid ${COLORS.border}`,
                              background: '#ffffff05',
                              color: COLORS.muted,
                              fontSize: '18px',
                              lineHeight: '1',
                              cursor: 'pointer',
                            }}
                            title={isExpanded ? 'Details ausblenden' : 'Details anzeigen'}
                          >
                            {isExpanded ? '▾' : '▸'}
                          </button>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {renderRequestStatusBadge(row.status, row.status, row.brokerStatus)}
                        </td>
                        <td style={{ padding: '12px', color: COLORS.text, fontSize: '13px' }}>
                          {row.openAt ? formatDateTime(row.openAt) : '—'}
                        </td>
                        <td style={{ padding: '12px', color: COLORS.text, fontSize: '13px', fontWeight: '700' }}>
                          {row.symbol}
                        </td>
                        <td style={{ padding: '12px', color: COLORS.text, fontSize: '13px' }}>
                          {row.strike != null ? formatGermanStrike(row.strike) : '—'}
                        </td>
                        <td style={{ padding: '12px', color: COLORS.text, fontSize: '13px' }}>
                          {row.entryPrice != null
                            ? `${formatGermanPrice(row.entryPrice)} · ${formatSignedQuantity(row.quantity, row.entryAction)}`
                            : formatSignedQuantity(row.quantity, row.entryAction)}
                        </td>
                        <td style={{ padding: '12px', fontSize: '13px', textAlign: 'right', color: pnlColor }}>
                          <div style={{ fontWeight: '700' }}>
                            {row.livePnl != null ? formatGermanPrice(row.livePnl) : '—'}
                          </div>
                          <div style={{ fontSize: '11px', marginTop: '4px', color: pnlColor }}>
                            {row.livePnlPercent != null ? `${row.livePnlPercent > 0 ? '+' : ''}${formatGermanPercent(row.livePnlPercent)}%` : '—'}
                          </div>
                        </td>
                        <td style={{ padding: '12px', color: COLORS.text, fontSize: '13px' }}>
                          {row.twsTargetLabel}
                        </td>
                        <td style={{ padding: '12px', color: COLORS.text, fontSize: '13px' }}>
                          {row.optionsProfile}
                        </td>
                        <td style={{ padding: '12px', color: COLORS.text, fontSize: '13px' }}>
                          <div>
                            {row.takeProfitPercent != null ? `TP ${formatGermanPrice(row.takeProfitPercent)}%` : 'TP aus'}
                            {' · '}
                            {row.stopLossPercent != null ? `SL ${formatGermanPrice(row.stopLossPercent)}%` : 'SL aus'}
                          </div>
                          <div style={{ color: COLORS.muted, fontSize: '11px', marginTop: '4px' }}>
                            {`${row.exitAttempts ?? '—'}x · ${row.exitWaitSeconds ?? '—'}s · ${row.exitAdjustment != null ? formatGermanPrice(row.exitAdjustment) : '—'}`}
                            {row.exitMarketOnLastAttempt ? ' · letzter Versuch MKT' : ''}
                          </div>
                        </td>
                        <td style={{ padding: '12px', color: COLORS.text, fontSize: '13px' }}>
                          <div>{row.orderId !== '—' ? `ID ${row.orderId}` : '—'}</div>
                          <div style={{ color: COLORS.muted, fontSize: '11px', marginTop: '4px' }}>
                            {renderTradeStatusLabel(row.status, row.brokerStatus)}
                            {row.brokerStatus && row.brokerStatus !== row.status ? ` · ${row.brokerStatus}` : ''}
                          </div>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', flexWrap: 'nowrap' }}>
                            {row.isStockTrade && row.canClose ? (
                              <Btn
                                color="ghost"
                                size="sm"
                                onClick={() => startEditStockPosition(row)}
                                disabled={closingTradeId === row.tradeId || deletingTradeId === row.tradeId}
                              >
                                Bearbeiten
                              </Btn>
                            ) : null}
                            {row.canClose ? (
                              <Btn
                                color="red"
                                size="sm"
                                title="Schließen"
                                aria-label={`${row.symbol || 'Trade'} schließen`}
                                onClick={() => setCloseTradeDialog({
                                  tradeId: row.tradeId,
                                  symbol: row.symbol,
                                  optionsProfile: row.optionsProfile,
                                  contractLabel: row.contractLabel,
                                })}
                                disabled={closingTradeId === row.tradeId || deletingTradeId === row.tradeId}
                                style={{
                                  minWidth: '34px',
                                  width: '34px',
                                  minHeight: '30px',
                                  padding: 0,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  boxSizing: 'border-box',
                                  fontSize: '15px',
                                  lineHeight: 1,
                                }}
                              >
                                {closingTradeId === row.tradeId ? '…' : 'X'}
                              </Btn>
                            ) : null}
                            {row.canCancel ? (
                              <Btn
                                color="ghost"
                                size="sm"
                                onClick={() => cancelAutomationTrade(row.tradeId)}
                                disabled={closingTradeId === row.tradeId || deletingTradeId === row.tradeId}
                              >
                                {closingTradeId === row.tradeId ? 'Breche ab ...' : 'Abbrechen'}
                              </Btn>
                            ) : null}
                            {!row.canClose && row.canResetClose ? (
                              <Btn
                                color="ghost"
                                size="sm"
                                onClick={() => resetAutomationTradeCloseStatus(row.tradeId)}
                                disabled={closingTradeId === row.tradeId || deletingTradeId === row.tradeId}
                              >
                                {closingTradeId === row.tradeId ? 'Freigeben ...' : 'Freigeben'}
                              </Btn>
                            ) : null}
                            {row.entryCancelStatus ? (
                              <span style={{ color: COLORS.muted, fontSize: '12px' }}>
                                {String(row.entryCancelStatus).toLowerCase() === 'requested' || String(row.entryCancelStatus).toLowerCase() === 'processing'
                                  ? 'Abbruch läuft'
                                  : row.entryCancelStatus}
                              </span>
                            ) : row.closeStatus ? (
                              <span style={{ color: COLORS.muted, fontSize: '12px' }}>{row.closeStatus}</span>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                      {isExpanded ? (
                        <tr style={{ borderBottom: `1px solid ${COLORS.border}55` }}>
                          <td colSpan={12} style={{ padding: '0 12px 14px 12px' }}>
                            <div
                              style={{
                                marginLeft: '28px',
                                border: `1px solid ${COLORS.border}`,
                                borderRadius: '16px',
                                background: '#0b151d',
                                padding: '16px',
                                display: 'grid',
                                gap: '14px',
                              }}
                            >
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
                                {[
                                  ['Side', signedQuantity < 0 ? 'SHORT' : 'LONG', COLORS.text],
                                  ['C/P', row.right || '—', COLORS.text],
                                  ['Strategie', row.optionsProfile, COLORS.text],
                                  ['Hold', holdDays, COLORS.text],
                                  ['Commissions', commissionTotal != null ? formatMoneyValue(commissionTotal, row.currency) : '—', commissionTotal != null ? COLORS.text : COLORS.muted],
                                  ['Max Win', formatRiskValue(optionRisk?.maxWin, row.currency), optionRisk?.maxWin === 'UNLIMITED' ? COLORS.green : valueColor(optionRisk?.maxWin)],
                                  ['Max Loss', formatRiskValue(optionRisk?.maxLoss, row.currency), optionRisk?.maxLoss === 'UNLIMITED' ? COLORS.red : valueColor(optionRisk?.maxLoss)],
                                  ['Live P&L', row.livePnl != null ? formatMoneyValue(row.livePnl, row.currency) : '—', pnlColor],
                                  ['Marktwert', markValue != null ? formatMoneyValue(markValue, row.currency) : '—', valueColor(markValue)],
                                ].map(([label, value, color]) => (
                                  <div key={`${row.tradeId}-${label}`} style={{ padding: '10px 12px', border: `1px solid ${COLORS.border}`, borderRadius: '10px', background: '#ffffff05' }}>
                                    <div style={{ fontSize: '11px', color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
                                    <div style={{ marginTop: '4px', fontWeight: 700, color }}>{value}</div>
                                    {label === 'Live P&L' && row.markPrice != null ? (
                                      <div style={{ marginTop: '4px', fontSize: '11px', color: COLORS.muted }}>
                                        Mark: {formatMoneyValue(row.markPrice, row.currency)}
                                      </div>
                                    ) : null}
                                  </div>
                                ))}
                              </div>

                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                <div style={{ display: 'grid', gap: '10px', minWidth: '260px', flex: '1 1 420px' }}>
                                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    <Btn
                                      color="ghost"
                                      size="sm"
                                      onClick={() => (editingTradeId === row.tradeId ? cancelEditExit(row.tradeId) : startEditExit(row))}
                                      disabled={closingTradeId === row.tradeId || deletingTradeId === row.tradeId || savingExitTradeId === row.tradeId}
                                    >
                                      {editingTradeId === row.tradeId ? 'Editor schließen' : 'Bearbeiten'}
                                    </Btn>
                                    {row.canDelete ? (
                                      <Btn
                                        color="ghost"
                                        size="sm"
                                        onClick={() => deleteAutomationTrade(row.tradeId)}
                                        disabled={closingTradeId === row.tradeId || deletingTradeId === row.tradeId}
                                      >
                                        {deletingTradeId === row.tradeId ? 'Entferne ...' : 'Aus Automation entfernen'}
                                      </Btn>
                                    ) : null}
                                  </div>
                                  {renderExitConfiguration(row)}
                                </div>
                                <div style={{ color: COLORS.muted, fontSize: '12px' }}>
                                  Kontrakt: <span style={{ color: COLORS.text }}>{row.contractLabel || '—'}</span>
                                </div>
                              </div>

                              {(row.markPrice != null || detailLegRows.length > 0) ? (
                                <div style={{ padding: '12px 14px', border: `1px solid ${COLORS.border}`, borderRadius: '12px', background: '#0f1d23' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px' }}>
                                    <div style={{ fontSize: '13px', fontWeight: 700, color: COLORS.text }}>Leg-Marks</div>
                                    <div style={{ fontSize: '11px', color: COLORS.muted }}>
                                      Quelle: {detailLegRows.some((leg) => leg.source && leg.source !== 'contract') ? 'ib_agent' : 'Kontrakt'}
                                    </div>
                                  </div>
                                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead style={{ background: '#ffffff08' }}>
                                      <tr>
                                        <th style={{ textAlign: 'left', padding: '8px 10px', color: COLORS.muted, fontSize: '11px' }}>Leg</th>
                                        <th style={{ textAlign: 'right', padding: '8px 10px', color: COLORS.muted, fontSize: '11px' }}>Net Qty</th>
                                        <th style={{ textAlign: 'right', padding: '8px 10px', color: COLORS.muted, fontSize: '11px' }}>Multiplier</th>
                                        <th style={{ textAlign: 'right', padding: '8px 10px', color: COLORS.muted, fontSize: '11px' }}>Bid</th>
                                        <th style={{ textAlign: 'right', padding: '8px 10px', color: COLORS.muted, fontSize: '11px' }}>Ask</th>
                                        <th style={{ textAlign: 'right', padding: '8px 10px', color: COLORS.muted, fontSize: '11px' }}>Last</th>
                                        <th style={{ textAlign: 'right', padding: '8px 10px', color: COLORS.muted, fontSize: '11px' }}>Mark</th>
                                        <th style={{ textAlign: 'left', padding: '8px 10px', color: COLORS.muted, fontSize: '11px' }}>Quelle</th>
                                        <th style={{ textAlign: 'right', padding: '8px 10px', color: COLORS.muted, fontSize: '11px' }}>FX</th>
                                        <th style={{ textAlign: 'right', padding: '8px 10px', color: COLORS.muted, fontSize: '11px' }}>Leg-Marktwert {row.currency}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {detailLegRows.map((leg) => (
                                        <tr key={leg.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                                          <td style={{ padding: '9px 10px', color: COLORS.text }}>{leg.label}</td>
                                          <td style={{ padding: '9px 10px', textAlign: 'right', color: COLORS.text }}>{formatCompactNumberValue(leg.netQty, 4)}</td>
                                          <td style={{ padding: '9px 10px', textAlign: 'right', color: COLORS.text }}>{formatCompactNumberValue(leg.multiplier, 2)}</td>
                                          <td style={{ padding: '9px 10px', textAlign: 'right', color: leg.bid != null ? COLORS.text : COLORS.muted }}>{leg.bid != null ? formatMoneyValue(leg.bid, row.currency) : '—'}</td>
                                          <td style={{ padding: '9px 10px', textAlign: 'right', color: leg.ask != null ? COLORS.text : COLORS.muted }}>{leg.ask != null ? formatMoneyValue(leg.ask, row.currency) : '—'}</td>
                                          <td style={{ padding: '9px 10px', textAlign: 'right', color: leg.lastPrice != null ? COLORS.text : COLORS.muted }}>{leg.lastPrice != null ? formatMoneyValue(leg.lastPrice, row.currency) : '—'}</td>
                                          <td style={{ padding: '9px 10px', textAlign: 'right', color: leg.markPrice != null ? COLORS.text : COLORS.muted }}>{leg.markPrice != null ? formatMoneyValue(leg.markPrice, row.currency) : '—'}</td>
                                          <td style={{ padding: '9px 10px', color: COLORS.text }}>{leg.source || '—'}</td>
                                          <td style={{ padding: '9px 10px', textAlign: 'right', color: leg.fxRate != null ? COLORS.text : COLORS.muted }}>{leg.fxRate != null ? formatCompactNumberValue(leg.fxRate, 5) : '—'}</td>
                                          <td style={{ padding: '9px 10px', textAlign: 'right', color: leg.marketValue != null ? valueColor(leg.marketValue) : COLORS.muted, fontWeight: 700 }}>{leg.marketValue != null ? formatMoneyValue(leg.marketValue, row.currency) : '—'}</td>
                                        </tr>
                                      ))}
                                      {detailLegRows.length === 0 ? (
                                        <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                                          <td colSpan={10} style={{ padding: '9px 10px', color: COLORS.muted }}>Keine Leg-Daten verfügbar.</td>
                                        </tr>
                                      ) : null}
                                    </tbody>
                                  </table>
                                </div>
                              ) : null}

                              {optionProfile ? (
                                <div style={{ padding: '14px', border: `1px solid ${COLORS.border}`, borderRadius: '14px', background: '#0e2026' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '12px' }}>
                                    <div>
                                      <div style={{ fontSize: '16px', fontWeight: 700, color: COLORS.text }}>Optionsprofil</div>
                                      <div style={{ marginTop: '4px', fontSize: '12px', color: COLORS.muted }}>
                                        {optionProfile.mode === 'today'
                                          ? 'Heute ~ auf Basis von Live P&L und Kurs'
                                          : `Payoff bei Verfall ${optionProfile.expiry ? `(${formatDateValue(optionProfile.expiry)})` : ''}`}
                                      </div>
                                      <div style={{ marginTop: '8px', display: 'inline-flex', padding: '6px 10px', borderRadius: '999px', border: `1px solid ${COLORS.border}`, background: '#ffffff05', fontSize: '12px', fontWeight: 700, color: COLORS.text }}>
                                        {row.optionsProfile}
                                      </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                      {optionProfileSupportsToday ? (
                                        <div style={{ display: 'inline-flex', border: `1px solid ${COLORS.border}`, borderRadius: '999px', overflow: 'hidden', background: '#ffffff05' }}>
                                          <button
                                            type="button"
                                            onClick={() => setOptionProfileModes((prev) => ({ ...prev, [row.tradeId]: 'expiry' }))}
                                            style={{ border: 'none', background: optionProfileMode === 'expiry' ? `${COLORS.cyan}22` : 'transparent', color: optionProfileMode === 'expiry' ? COLORS.cyan : COLORS.muted, padding: '6px 10px', fontSize: '12px', cursor: 'pointer' }}
                                          >
                                            Verfall
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => setOptionProfileModes((prev) => ({ ...prev, [row.tradeId]: 'today' }))}
                                            style={{ border: 'none', borderLeft: `1px solid ${COLORS.border}`, background: optionProfileMode === 'today' ? `${COLORS.cyan}22` : 'transparent', color: optionProfileMode === 'today' ? COLORS.cyan : COLORS.muted, padding: '6px 10px', fontSize: '12px', cursor: 'pointer' }}
                                          >
                                            Heute ~
                                          </button>
                                        </div>
                                      ) : null}
                                      <div style={{ padding: '6px 10px', borderRadius: '999px', border: `1px solid ${COLORS.border}`, background: '#ffffff05', fontSize: '12px', color: COLORS.text }}>
                                        Break-even: {optionProfile.breakEvens.length > 0 ? optionProfile.breakEvens.map((value) => formatCompactNumberValue(value, 2)).join(' / ') : '—'}
                                      </div>
                                      {optionBreakEvenDistance ? (
                                        <div style={{ padding: '6px 10px', borderRadius: '999px', border: `1px solid ${COLORS.border}`, background: '#ffffff05', fontSize: '12px', color: COLORS.text }}>
                                          Abstand zu BE: {formatCompactNumberValue(optionBreakEvenDistance.absDistance, 2)}
                                          {optionBreakEvenDistance.percent != null ? ` (${formatCompactNumberValue(optionBreakEvenDistance.percent, 2)}%)` : ''}
                                          {` ${optionBreakEvenDistance.direction}`}
                                        </div>
                                      ) : null}
                                      <div style={{ padding: '6px 10px', borderRadius: '999px', border: `1px solid ${COLORS.border}`, background: '#ffffff05', fontSize: '12px', color: COLORS.text }}>
                                        Strikes: {optionProfile.strikes.map((value) => formatCompactNumberValue(value, 2)).join(' / ')}
                                      </div>
                                      {row.currentUnderlyingPrice != null ? (
                                        <div style={{ padding: '6px 10px', borderRadius: '999px', border: `1px solid ${COLORS.border}`, background: '#ffffff05', fontSize: '12px', color: COLORS.text }}>
                                          Underlying: {formatMoneyValue(row.currentUnderlyingPrice, row.underlyingPriceCurrency)}
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                  <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: '12px', background: '#09161b', padding: '10px 10px 4px', marginBottom: '12px' }}>
                                    <OptionProfileChart profile={optionProfile} currency={row.currency} COLORS={COLORS} valueColor={valueColor} scenarioPrice={scenarioPrice} />
                                  </div>
                                  {optionProfile.currentUnderlyingPrice != null ? (
                                    <div style={{ display: 'grid', gap: '8px', marginBottom: '12px', padding: '10px 12px', border: `1px solid ${COLORS.border}`, borderRadius: '12px', background: '#ffffff05' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                                        <div style={{ fontSize: '12px', color: COLORS.muted }}>
                                          Szenario-Kurs: <span style={{ color: COLORS.text, fontWeight: 700 }}>{formatMoneyValue(scenarioPrice, row.underlyingPriceCurrency)}</span>
                                        </div>
                                        <div style={{ fontSize: '12px', color: scenarioPnl != null ? valueColor(scenarioPnl) : COLORS.muted, fontWeight: 700 }}>
                                          Verfall beim Szenario: {scenarioPnl != null ? formatMoneyValue(scenarioPnl, row.currency) : '—'}
                                        </div>
                                      </div>
                                      <input
                                        type="range"
                                        min={optionProfile.minPrice}
                                        max={optionProfile.maxPrice}
                                        step={Math.max((optionProfile.maxPrice - optionProfile.minPrice) / 200, 0.01)}
                                        value={scenarioPrice}
                                        onChange={(event) => setOptionProfileScenarioPrices((current) => ({ ...current, [row.tradeId]: Number(event.target.value) }))}
                                        style={{ width: '100%' }}
                                      />
                                    </div>
                                  ) : null}
                                  {optionGreeks ? (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px', marginBottom: '12px' }}>
                                      {[
                                        ['Delta ~', optionGreeks.delta],
                                        ['Gamma ~', optionGreeks.gamma],
                                        ['Theta/Tag ~', optionGreeks.theta],
                                        ['Vega ~', optionGreeks.vega],
                                      ].map(([label, value]) => (
                                        <div key={`${row.tradeId}-${label}`} style={{ padding: '10px 12px', borderRadius: '10px', border: `1px solid ${COLORS.border}`, background: '#ffffff05' }}>
                                          <div style={{ fontSize: '11px', color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
                                          <div style={{ marginTop: '4px', fontWeight: 700, color: valueColor(Number(value || 0)) }}>
                                            {formatCompactNumberValue(value, 2)}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : null}
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px' }}>
                                    {optionProfile.legs.map((leg, index) => (
                                      <div key={`${row.tradeId}-leg-${index}`} style={{ padding: '10px 12px', borderRadius: '10px', border: `1px solid ${COLORS.border}`, background: '#ffffff05' }}>
                                        <div style={{ fontSize: '11px', color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Leg {index + 1}</div>
                                        <div style={{ marginTop: '4px', fontWeight: 700, color: leg.netQty > 0 ? COLORS.green : COLORS.red }}>
                                          {leg.netQty > 0 ? '+' : ''}{formatCompactNumberValue(leg.netQty, 0)} {row.symbol} {formatCompactNumberValue(leg.strike, 0)}{leg.type}
                                        </div>
                                        <div style={{ marginTop: '4px', fontSize: '12px', color: COLORS.muted }}>
                                          Multiplier {formatCompactNumberValue(getDetailMultiplier(leg), 0)}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : null}

                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead style={{ background: '#ffffff08' }}>
                                  <tr>
                                    <th style={{ textAlign: 'left', padding: '8px 10px', color: COLORS.muted, fontSize: '11px' }}>Action</th>
                                    <th style={{ textAlign: 'left', padding: '8px 10px', color: COLORS.muted, fontSize: '11px' }}>Type</th>
                                    <th style={{ textAlign: 'left', padding: '8px 10px', color: COLORS.muted, fontSize: '11px' }}>O/C</th>
                                    <th style={{ textAlign: 'left', padding: '8px 10px', color: COLORS.muted, fontSize: '11px' }}>Expire</th>
                                    <th style={{ textAlign: 'left', padding: '8px 10px', color: COLORS.muted, fontSize: '11px' }}>Strike</th>
                                    <th style={{ textAlign: 'left', padding: '8px 10px', color: COLORS.muted, fontSize: '11px' }}>Date</th>
                                    <th style={{ textAlign: 'left', padding: '8px 10px', color: COLORS.muted, fontSize: '11px' }}>Time</th>
                                    <th style={{ textAlign: 'right', padding: '8px 10px', color: COLORS.muted, fontSize: '11px' }}>Size</th>
                                    <th style={{ textAlign: 'right', padding: '8px 10px', color: COLORS.muted, fontSize: '11px' }}>Pos.</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                                    <td style={{ padding: '9px 10px', color: COLORS.text }}>{String(row.entryAction || '').trim().toUpperCase() === 'SELL' ? 'SELL' : 'BUY'}</td>
                                    <td style={{ padding: '9px 10px', color: COLORS.text }}>{row.right || '—'}</td>
                                    <td style={{ padding: '9px 10px', color: COLORS.text }}>O</td>
                                    <td style={{ padding: '9px 10px', color: COLORS.text }}>{formatDateValue(row.expiry)}</td>
                                    <td style={{ padding: '9px 10px', color: COLORS.text }}>{formatCompactNumberValue(row.strike, 0)}</td>
                                    <td style={{ padding: '9px 10px', color: COLORS.text }}>{detailDate ? detailDate.toLocaleDateString('de-DE') : '—'}</td>
                                    <td style={{ padding: '9px 10px', color: COLORS.text }}>{detailDate ? detailDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}</td>
                                    <td style={{ padding: '9px 10px', textAlign: 'right', color: COLORS.text }}>{formatCompactNumberValue(signedQuantity, 0)}</td>
                                    <td style={{ padding: '9px 10px', textAlign: 'right', color: COLORS.text }}>{formatCompactNumberValue(signedQuantity, 0)}</td>
                                  </tr>
                                  <tr>
                                    <td style={{ padding: '9px 10px', color: COLORS.muted, fontSize: '11px' }}>Price</td>
                                    <td style={{ padding: '9px 10px', color: COLORS.muted, fontSize: '11px' }}>Return</td>
                                    <td style={{ padding: '9px 10px', color: COLORS.muted, fontSize: '11px' }}>Commission</td>
                                    <td style={{ padding: '9px 10px', color: COLORS.muted, fontSize: '11px' }}>Fees</td>
                                    <td style={{ padding: '9px 10px', color: COLORS.muted, fontSize: '11px' }}>FX</td>
                                    <td style={{ padding: '9px 10px', color: COLORS.muted, fontSize: '11px' }}>Currency</td>
                                    <td style={{ padding: '9px 10px', color: COLORS.muted, fontSize: '11px' }}>Trade Type</td>
                                    <td colSpan={2} style={{ padding: '9px 10px', color: COLORS.muted, fontSize: '11px' }}>Flags</td>
                                  </tr>
                                  <tr>
                                    <td style={{ padding: '9px 10px', color: COLORS.text }}>{formatMoneyValue(row.entryPrice, row.currency)}</td>
                                    <td style={{ padding: '9px 10px', color: valueColor(row.livePnl), fontWeight: 700 }}>{formatMoneyValue(row.livePnl, row.currency)}</td>
                                    <td style={{ padding: '9px 10px', color: COLORS.muted }}>—</td>
                                    <td style={{ padding: '9px 10px', color: COLORS.muted }}>—</td>
                                    <td style={{ padding: '9px 10px', color: COLORS.text }}>1.00000</td>
                                    <td style={{ padding: '9px 10px', color: COLORS.text }}>{row.currency}</td>
                                    <td style={{ padding: '9px 10px', color: COLORS.text }}>Automation</td>
                                    <td colSpan={2} style={{ padding: '9px 10px', color: COLORS.muted }}>—</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      </div>
      {closeTradeDialog ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(4, 10, 16, 0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200,
            padding: '20px',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '460px',
              borderRadius: '18px',
              border: `1px solid ${COLORS.border}`,
              background: '#0d1822',
              boxShadow: '0 24px 80px rgba(0,0,0,0.45)',
              padding: '22px',
              display: 'grid',
              gap: '16px',
            }}
          >
            <div>
              <div style={{ color: COLORS.text, fontSize: '18px', fontWeight: 800 }}>
                Trade schließen
              </div>
              <div style={{ color: COLORS.muted, fontSize: '13px', marginTop: '8px', lineHeight: 1.6 }}>
                {closeTradeDialog.symbol} · {closeTradeDialog.optionsProfile}
              </div>
              <div style={{ color: COLORS.muted, fontSize: '12px', marginTop: '4px', lineHeight: 1.5 }}>
                {closeTradeDialog.contractLabel}
              </div>
            </div>
            <div style={{ color: COLORS.text, fontSize: '14px', lineHeight: 1.6 }}>
              Wie soll der Close-Trade gesendet werden?
            </div>
            <div style={{ display: 'grid', gap: '10px' }}>
              <button
                type="button"
                onClick={() => closeAutomationTrade(closeTradeDialog.tradeId, 'limit')}
                disabled={closingTradeId === closeTradeDialog.tradeId}
                style={{
                  textAlign: 'left',
                  borderRadius: '14px',
                  border: `1px solid ${COLORS.border}`,
                  background: '#ffffff08',
                  color: COLORS.text,
                  padding: '14px 16px',
                  cursor: closingTradeId === closeTradeDialog.tradeId ? 'default' : 'pointer',
                }}
              >
                <div style={{ fontSize: '14px', fontWeight: 700 }}>Limit</div>
                <div style={{ fontSize: '12px', color: COLORS.muted, marginTop: '4px' }}>
                  Sendet den Close als Limit-Order mit der bestehenden Preislogik.
                </div>
              </button>
              <button
                type="button"
                onClick={() => closeAutomationTrade(closeTradeDialog.tradeId, 'market')}
                disabled={closingTradeId === closeTradeDialog.tradeId}
                style={{
                  textAlign: 'left',
                  borderRadius: '14px',
                  border: `1px solid ${COLORS.border}`,
                  background: '#ffffff08',
                  color: COLORS.text,
                  padding: '14px 16px',
                  cursor: closingTradeId === closeTradeDialog.tradeId ? 'default' : 'pointer',
                }}
              >
                <div style={{ fontSize: '14px', fontWeight: 700 }}>Market</div>
                <div style={{ fontSize: '12px', color: COLORS.muted, marginTop: '4px' }}>
                  Sendet den Close sofort als Market-Order.
                </div>
              </button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <Btn
                color="ghost"
                size="sm"
                onClick={() => setCloseTradeDialog(null)}
                disabled={closingTradeId === closeTradeDialog.tradeId}
              >
                Abbrechen
              </Btn>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
